import galleryDogsPlaying from "@/assets/gallery-dogs-playing.jpg";
import galleryDogsYard from "@/assets/gallery-dogs-yard.jpg";
import galleryGermanShepherd from "@/assets/gallery-german-shepherd.jpg";
import galleryEstate from "@/assets/gallery-estate-fence.jpg";
import galleryHound from "@/assets/gallery-hound-fence.jpg";
import galleryBackyardGate from "@/assets/gallery-backyard-gate.jpg";
import galleryDoubleGate from "@/assets/gallery-double-gate.jpg";
import galleryDeerHill from "@/assets/gallery-deer-fence-hill.jpg";
import galleryDeerLandscape from "@/assets/gallery-deer-fence-landscape.jpg";
import hiddenFence from "@/assets/hidden-fence.jpeg";
import deerFenceReal from "@/assets/deer-fence.png";
import gardenFenceReal from "@/assets/garden-fence.jpeg";

export type Region = "Northeast" | "Mid-Atlantic" | "Southeast";

export interface LocationData {
  slug: string;
  city: string;
  state: string;
  region: Region;
  heroImage: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  wildlifeNotes: string;
  climateNotes: string;
  lotNotes: string;
  whyChoose: { title: string; description: string }[];
  nearbyAreas: string[];
  localFaqs: { q: string; a: string }[];
  lat: number;
  lng: number;
  /**
   * Optional regional cost-of-install modifier applied to base service pricing.
   * 1.0 = national average baseline. e.g. 1.15 for high-COL Northeast suburbs,
   * 0.95 for lower-COL Southeast metros. Defaults to regional average if missing.
   */
  priceModifier?: number;
  /** Short note about local permits / HOA tendencies. Falls back to regional default. */
  permitNotes?: string;
  /** Short note about install seasonality and lead times for the region. */
  seasonalNotes?: string;
}

/**
 * Fallback regional defaults so every location has rich content even without
 * per-location overrides. Regional averages reflect cost-of-install + permit culture.
 */
export const regionalDefaults: Record<
  Region,
  { priceModifier: number; permitNotes: string; seasonalNotes: string }
> = {
  Northeast: {
    priceModifier: 1.15,
    permitNotes:
      "Fence height, setbacks and approval requirements vary by municipality and property. Check with your local authority and HOA before installation. Obtaining permits and submitting HOA paperwork are the homeowner's responsibility; specification sheets and renderings are available on request for your application.",
    seasonalNotes:
      "Weather, ground conditions, access and required approvals can affect scheduling. Your written proposal confirms the installation timing for your property.",
  },
  "Mid-Atlantic": {
    priceModifier: 1.1,
    permitNotes:
      "Permit and HOA requirements vary by municipality and property. Confirm applicable height and setback rules with the local authority. Obtaining permits and submitting HOA paperwork are the homeowner's responsibility; specification sheets and renderings are available on request.",
    seasonalNotes:
      "Weather, ground conditions, access and required approvals can affect scheduling. Your written proposal confirms the installation timing for your property.",
  },
  Southeast: {
    priceModifier: 0.95,
    permitNotes:
      "Check local height, setback and HOA requirements for your property before installation. Obtaining permits and submitting HOA paperwork are the homeowner's responsibility; specification sheets and renderings are available on request.",
    seasonalNotes:
      "Weather, ground conditions, access and required approvals can affect scheduling. Your written proposal confirms the installation timing for your property.",
  },
};

const sharedFaqBase = (city: string, state: string) => [
  {
    q: `Do you install fences in ${city}?`,
    a: `Yes — ${city} and the surrounding ${state} area is one of our active service regions. We schedule a free virtual consultation, build a custom plan for your property, and confirm your exact install timeline in your written proposal.`,
  },
  {
    q: `Do I need a permit to install a fence in ${city}?`,
    a: `Requirements vary by municipality and HOA. Check fence height, setbacks and any required approvals with the authority for your ${city}-area property. Obtaining permits and submitting HOA paperwork are the homeowner's responsibility; specification sheets and renderings are available on request.`,
  },
  {
    q: `What's the best time of year to install a fence in ${city}?`,
    a: `Timing for your ${city}-area property depends on weather, ground conditions, access and required approvals. Your written proposal confirms the schedule; there is no fixed seasonal lead time.`,
  },
];

export const locations: LocationData[] = [
  // ---------------- Northeast ----------------
  {
    slug: "metrowest-ma",
    city: "MetroWest Massachusetts",
    state: "MA",
    region: "Northeast",
    heroImage: galleryEstate,
    metaTitle: "Fence Installation in MetroWest MA | Modern Fence Install",
    metaDescription:
      "Premium non-electric dog, deer, and garden fence installation across MetroWest Massachusetts. Built for one-acre-plus wooded properties. Free consultation.",
    intro:
      "MetroWest is where Boston commuters land when they want acreage, mature trees, and quiet roads. Wellesley, Concord, Weston, Dover, Sherborn, Sudbury, Lincoln — these towns are full of one-acre-plus properties facing real wildlife pressure and real landscape investment to protect. We specialize in these wooded suburbs and rural-edge towns.",
    wildlifeNotes:
      "Eastern white-tailed deer have completely overrun MetroWest gardens, and coyote sightings are now routine from Lincoln through Sherborn. A real physical fence is the single most effective intervention for both deer browsing and family-pet safety.",
    climateNotes:
      "New England winters are tough on fencing. Our galvanized cables, posts, and tensioners are rated for heavy snow loads and decades of freeze-thaw cycles.",
    lotNotes:
      "MetroWest properties typically run from 1 acre to 5+ acres, often heavily wooded with stone walls, conservation easements, and irregular boundaries. We custom-engineer every layout to follow the land instead of fighting it.",
    whyChoose: [
      { title: "Suburban-acreage focus", description: "Built for the 1-to-10-acre wooded lots that define MetroWest." },
      { title: "Conservation-aware installs", description: "We understand wetlands setbacks and town conservation rules across MetroWest." },
      { title: "Discreet by design", description: "Nearly invisible from the road — preserves the New England character your town protects." },
      { title: "Built for New England weather", description: "Galvanized hardware that holds up through 30+ Massachusetts winters." },
    ],
    nearbyAreas: ["Wellesley", "Concord", "Weston", "Dover", "Sherborn", "Sudbury", "Lincoln", "Needham", "Carlisle", "Wayland", "Natick", "Westwood"],
    localFaqs: [
      ...sharedFaqBase("MetroWest Massachusetts", "Massachusetts"),
      {
        q: "Which Massachusetts towns do you serve?",
        a: "We specialize in MetroWest and rural-edge suburbs — Wellesley, Concord, Weston, Dover, Sudbury, Lincoln, and surrounding towns — where homeowners typically have an acre or more of land and need a real physical fence.",
      },
      {
        q: "Will the fence survive a New England winter?",
        a: "Yes. Our cables, posts, and tensioners are galvanized for salt and moisture resistance, and the system is engineered to flex without snapping under heavy snow and ice loads.",
      },
    ],
    lat: 42.3370,
    lng: -71.2092,
  },
  {
    slug: "hartford-county-ct",
    city: "Hartford County",
    state: "CT",
    region: "Northeast",
    heroImage: galleryEstate,
    metaTitle: "Fence Installation in Hartford County, CT | Modern Fence Install",
    metaDescription:
      "Premium non-electric dog, deer, and garden fence installation across Hartford County, CT suburbs. Built for one-acre-plus properties. Free consultation.",
    intro:
      "Hartford County's suburban ring-3 — West Hartford, Avon, Farmington, Simsbury, Glastonbury, Canton — has some of the densest deer populations in the Northeast and some of the most landscape-investment-heavy properties in central Connecticut. We focus on these one-acre-plus suburban and rural-edge properties, where a real fence actually solves the problem.",
    wildlifeNotes:
      "Connecticut's deer pressure is legendary, especially in the suburbs ringing Hartford. Add coyotes, fishers, and the occasional black bear, and a thoughtfully engineered fence becomes essential for both pets and gardens.",
    climateNotes:
      "Hartford's mix of cold winters and humid summers can be brutal on cheap fencing. Every component we install is rated for the full Connecticut weather spectrum.",
    lotNotes:
      "Many Greater Hartford lots are wooded, sloped, and irregular. We specialize in following natural contours rather than fighting them.",
    whyChoose: [
      { title: "Engineered for CT deer", description: "Heights and mesh sized for the deer pressure across Hartford County." },
      { title: "Works on slopes & wooded lots", description: "Our system follows terrain instead of requiring you to clear it." },
      { title: "Quiet, low-impact installs", description: "Most properties installed in 1–3 days with minimal landscape disruption." },
      { title: "Transparent pricing", description: "Flat per-foot quotes — no surprise change orders mid-project." },
    ],
    nearbyAreas: ["West Hartford", "Avon", "Farmington", "Glastonbury", "Simsbury", "Canton", "Bloomfield", "Wethersfield", "South Windsor", "Manchester"],
    localFaqs: [
      ...sharedFaqBase("Hartford County", "Connecticut"),
      {
        q: "Will a deer fence really keep Connecticut deer out?",
        a: "Yes. Our deer fences are sized to the pressure level in your specific neighborhood — usually 7.5 or 8 feet of nearly invisible mesh that deer simply won't try to jump.",
      },
      {
        q: "Are you a fence contractor in West Hartford, CT?",
        a: "Yes — West Hartford is one of our most-installed-in towns. We've put in dog, deer, and garden fences from the Bishops Corner / Buena Vista side through West Hill and the rural-edge lots along Mountain Road. We know the local permitting cadence and what tends to pass West Hartford zoning review.",
      },
      {
        q: "Do you install fences for West Hartford one-acre-plus lots?",
        a: "That's our sweet spot. Many of the West Hartford lots we install on run 1 to 4 acres, often with mature trees, a sloped backyard, and an existing landscape investment worth protecting. We engineer around the trees rather than clearing them.",
      },
    ],
    lat: 41.7658,
    lng: -72.6734,
  },
  {
    slug: "westchester-ny",
    city: "Westchester County",
    state: "NY",
    region: "Northeast",
    heroImage: galleryDeerLandscape,
    metaTitle: "Fence Installation in Westchester County, NY | Modern Fence Install",
    metaDescription:
      "Premium non-electric deer, dog, and garden fence installation across Westchester County, NY. Discreet designs for estates and suburban properties. Free consultation.",
    intro:
      "Westchester County has some of the highest deer densities in the country — and some of the most architecturally significant properties on the East Coast. From Bedford and Pound Ridge to Scarsdale and Bronxville, we install fence systems that protect your landscape investment without altering its character.",
    wildlifeNotes:
      "Deer browsing causes millions in landscape damage across Westchester every year. Coyote populations are also thriving from Yonkers to Yorktown. Our systems are tuned to the local wildlife reality.",
    climateNotes:
      "Westchester sees the full Northeast weather range. Our hardware is selected to handle salt-laden coastal air in Mamaroneck just as well as the colder microclimate up in Bedford.",
    lotNotes:
      "Lot sizes vary dramatically — from village lots in Larchmont to multi-acre estates in northern Westchester. We custom-engineer every install.",
    whyChoose: [
      { title: "Estate-grade installations", description: "Trusted on properties from village lots to 10+ acre estates." },
      { title: "HOA & ARB friendly", description: "Low-visual-impact design typically passes architectural review." },
      { title: "Real deer protection", description: "Engineered specifically for Westchester's intense deer pressure." },
      { title: "Discreet crews", description: "Professional, uniformed installers respectful of high-end properties." },
    ],
    nearbyAreas: ["Bedford", "Pound Ridge", "Scarsdale", "Bronxville", "Chappaqua", "Armonk", "Katonah", "Larchmont", "Rye", "Mount Kisco", "Yorktown", "Mamaroneck"],
    localFaqs: [
      ...sharedFaqBase("Westchester County", "New York"),
      {
        q: "Will a fence pass my Westchester ARB or HOA review?",
        a: "In our experience, yes — the nearly invisible profile of our systems is far more likely to pass review than traditional tall perimeter fencing. We can provide spec sheets and renderings to support your application.",
      },
      {
        q: "Do you install fences in Larchmont and Mamaroneck?",
        a: "Yes — Larchmont and Mamaroneck are core service towns for us. Many Larchmont lots are village-scale with mature plantings and tight neighbor sightlines, so our nearly invisible system is a particularly good fit. We handle the Larchmont village permit and any HOA / association review on a case-by-case basis.",
      },
      {
        q: "How fast can you install a fence in Larchmont?",
        a: "For a typical Larchmont village lot under half an acre, most installs run 1 to 2 days on-site once permits are in hand. End-to-end from first call to completed fence is usually 3 to 5 weeks in Larchmont.",
      },
    ],
    lat: 41.1220,
    lng: -73.7949,
  },
  {
    slug: "bergen-county-nj",
    city: "Bergen County",
    state: "NJ",
    region: "Northeast",
    heroImage: hiddenFence,
    metaTitle: "Fence Installation in Bergen County, NJ | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across Bergen County, NJ. Discreet, durable systems for Northern Jersey homes. Free virtual consultation.",
    intro:
      "From Ridgewood and Saddle River to Franklin Lakes and Tenafly, Bergen County homeowners want safety and beauty in equal measure. Our nearly invisible fence systems give you both — without the maintenance, bulk, or visual weight of traditional perimeter fencing.",
    wildlifeNotes:
      "Northern New Jersey's deer pressure is heavy, especially closer to the Ramapo foothills. Black bear sightings are also rising — another reason a real physical fence beats invisible electric systems.",
    climateNotes:
      "Bergen County weather swings from humid summers to ice storms in winter. Our hardware is selected to handle the full range without rust, sag, or failure.",
    lotNotes:
      "From compact borough lots to multi-acre properties in the western half of the county, we tailor every install to the parcel.",
    whyChoose: [
      { title: "Bergen-tested durability", description: "Our hardware shrugs off Northern Jersey humidity and ice." },
      { title: "Pet-safe by design", description: "No shocks, no batteries — just a real, reliable physical barrier." },
      { title: "Beautiful from the curb", description: "Maintains the aesthetic of upscale Bergen neighborhoods." },
      { title: "Clear scheduling", description: "Your Bergen County install date is confirmed in your written proposal." },
    ],
    nearbyAreas: ["Ridgewood", "Saddle River", "Franklin Lakes", "Tenafly", "Alpine", "Mahwah", "Wyckoff", "Ho-Ho-Kus", "Upper Saddle River", "Englewood Cliffs"],
    localFaqs: [
      ...sharedFaqBase("Bergen County", "New Jersey"),
      {
        q: "Is your fence better than an invisible electric dog fence?",
        a: "For most Bergen County families, yes. Electric fences rely on a collar and a shock — they don't keep wildlife out, they fail when batteries die, and they require training. Our physical fence works 24/7, every dog, no shocks.",
      },
    ],
    lat: 40.9598,
    lng: -74.0746,
  },
  {
    slug: "philadelphia-pa",
    city: "Philadelphia",
    state: "PA",
    region: "Northeast",
    heroImage: deerFenceReal,
    metaTitle: "Fence Installation in Philadelphia, PA | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across Greater Philadelphia and the Main Line. Discreet designs for suburban properties. Free consultation.",
    intro:
      "From the Main Line through Bucks and Chester counties, Greater Philadelphia is a patchwork of historic neighborhoods, wooded suburbs, and sprawling estates. Our fence systems are engineered to fit each one — keeping your dogs safe and the deer out without changing the look of your home.",
    wildlifeNotes:
      "Suburban Philadelphia has some of the densest deer populations in the Mid-Atlantic, particularly in Chester County and along the Main Line. Coyotes are increasingly common in the western suburbs as well.",
    climateNotes:
      "Philly's humid summers and freezing winters demand corrosion-resistant hardware. Every component we install is selected for the full seasonal range.",
    lotNotes:
      "From row-home backyards in the city to 5-acre Main Line lots, we right-size every install to the property.",
    whyChoose: [
      { title: "Main Line approved", description: "Low-profile designs that fit the character of historic suburbs." },
      { title: "Real deer control", description: "Sized correctly for the heavy browsing pressure in Chester & Bucks." },
      { title: "Permit-friendly", description: "Most Greater Philly municipalities approve our system without issue." },
      { title: "Clear turnaround", description: "Your confirmed timeline from consultation to completed install is set in writing." },
    ],
    nearbyAreas: ["Wayne", "Villanova", "Bryn Mawr", "Radnor", "Doylestown", "West Chester", "Newtown", "Chadds Ford", "Malvern", "Ambler", "Gladwyne"],
    localFaqs: [
      ...sharedFaqBase("Philadelphia", "Pennsylvania"),
      {
        q: "Do you install in historic Main Line neighborhoods?",
        a: "Absolutely. Our nearly invisible system is a great fit for properties with strict aesthetic standards because it largely disappears from the street view.",
      },
    ],
    lat: 39.9526,
    lng: -75.1652,
  },

  // ---------------- Mid-Atlantic ----------------
  {
    slug: "northern-virginia",
    city: "Northern Virginia",
    state: "VA",
    region: "Mid-Atlantic",
    heroImage: galleryHound,
    metaTitle: "Fence Installation in Northern Virginia | Modern Fence Install",
    metaDescription:
      "Premium non-electric dog, deer, and garden fence installation across Northern Virginia — Fairfax, Loudoun, Arlington, and beyond. Free consultation.",
    intro:
      "Northern Virginia's mix of established suburbs and exploding new development creates real wildlife conflict — and real demand for fences that look like they belong. Whether you're in Great Falls, McLean, Vienna, or out in Loudoun County's horse country, we have a system that fits.",
    wildlifeNotes:
      "Fairfax and Loudoun counties have some of the densest deer populations in Virginia. Add coyotes pushing in from the Blue Ridge foothills and the occasional fox or bobcat, and a real fence makes the difference.",
    climateNotes:
      "NoVA's humid summers, ice storms, and clay-heavy soil all factor into how we engineer your fence. Posts are sized and set for our soil conditions.",
    lotNotes:
      "From inside-the-Beltway townhome lots to 20-acre Loudoun farms, we tailor each install to the property and the goals.",
    whyChoose: [
      { title: "Built for NoVA clay", description: "Posts engineered and set for our heavy clay soils." },
      { title: "HOA-savvy", description: "We've helped homeowners get approval in dozens of Fairfax & Loudoun HOAs." },
      { title: "Estate-ready", description: "Trusted on properties from townhomes to multi-acre horse farms." },
      { title: "Discreet by design", description: "Practically disappears from the road — keeps curb appeal intact." },
    ],
    nearbyAreas: ["McLean", "Great Falls", "Vienna", "Oakton", "Reston", "Herndon", "Leesburg", "Ashburn", "Middleburg", "Purcellville", "Arlington", "Alexandria"],
    localFaqs: [
      ...sharedFaqBase("Northern Virginia", "Virginia"),
      {
        q: "Will the fence work on a wooded Fairfax County lot?",
        a: "Yes — wooded and partially wooded lots are one of our specialties. We follow the contour of the land and route around mature trees rather than clearing them.",
      },
    ],
    lat: 38.8462,
    lng: -77.3064,
  },
  {
    slug: "montgomery-county-md",
    city: "Montgomery County",
    state: "MD",
    region: "Mid-Atlantic",
    heroImage: galleryDeerLandscape,
    metaTitle: "Fence Installation in Montgomery County, MD | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across Montgomery County, Maryland — Bethesda, Potomac, Chevy Chase, and beyond. Free consultation.",
    intro:
      "From Bethesda and Chevy Chase to Potomac and Olney, Montgomery County has some of the most landscape-investment-heavy properties in the Mid-Atlantic — and some of the most aggressive deer populations. We design fence systems that protect both without compromising on aesthetics.",
    wildlifeNotes:
      "Montgomery County deer browsing is relentless, especially north and west of the Beltway. Our deer fence systems are sized appropriately and installed to disappear into wooded property edges.",
    climateNotes:
      "Maryland's humid summers and mixed winters require corrosion-resistant hardware throughout. Our galvanized components are built for the long haul.",
    lotNotes:
      "Lot sizes range from quarter-acre Bethesda parcels to multi-acre Potomac estates. We engineer every install to scale.",
    whyChoose: [
      { title: "Garden protection that works", description: "Stop the ongoing cost of deer-damaged landscaping and bulbs." },
      { title: "Quiet, professional crews", description: "Respectful, uniformed installers — minimal disruption." },
      { title: "Permit support", description: "We help you navigate Montgomery County permit and HOA requirements." },
      { title: "Beautiful finish", description: "Looks intentional — never industrial." },
    ],
    nearbyAreas: ["Bethesda", "Chevy Chase", "Potomac", "Rockville", "Kensington", "Silver Spring", "Olney", "Gaithersburg", "Darnestown", "Travilah"],
    localFaqs: [
      ...sharedFaqBase("Montgomery County", "Maryland"),
      {
        q: "Will this fence stop deer from destroying my garden?",
        a: "Yes — that's exactly what it's designed to do. We size the height to the deer pressure in your specific area, and the mesh is engineered so deer won't even attempt to push through.",
      },
    ],
    lat: 39.1547,
    lng: -77.2405,
  },
  // washington-dc removed — redirects to loudoun-county-va via slug alias in LocationDetail

  // ---------------- Southeast ----------------
  {
    slug: "atlanta-ga",
    city: "Atlanta",
    state: "GA",
    region: "Southeast",
    heroImage: galleryGermanShepherd,
    metaTitle: "Fence Installation in Atlanta, GA | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across Greater Atlanta. Designed for Georgia heat, red clay, and large suburban lots. Free consultation.",
    intro:
      "Greater Atlanta — from Buckhead and Sandy Springs to Roswell, Alpharetta, and Milton — has some of the largest suburban lots in the country. Our nearly invisible fence systems are built to handle Georgia heat, red clay, and the deer pressure that comes with all those wooded acres.",
    wildlifeNotes:
      "Northern Atlanta suburbs see steady deer browsing, plus growing coyote populations across Cherokee, Forsyth, and Fulton counties. A reliable physical fence is the most effective long-term answer.",
    climateNotes:
      "Atlanta's humidity, intense UV, and occasional ice storms all factor into our hardware spec. Every component is built for the Southeast climate.",
    lotNotes:
      "We're set up for the larger lots common across North Atlanta — efficient installs even on multi-acre wooded properties.",
    whyChoose: [
      { title: "Built for Georgia heat & UV", description: "UV-stable components that won't fade or fail." },
      { title: "Red clay expertise", description: "Posts set deep and correctly for our soil conditions." },
      { title: "Large-lot specialists", description: "Equipped to handle the multi-acre lots common up I-75 and GA-400." },
      { title: "Pet-safe & wildlife-effective", description: "Keeps dogs in and deer out — without batteries or shocks." },
    ],
    nearbyAreas: ["Buckhead", "Sandy Springs", "Roswell", "Alpharetta", "Milton", "Johns Creek", "Marietta", "East Cobb", "Brookhaven", "Dunwoody"],
    localFaqs: [
      ...sharedFaqBase("Atlanta", "Georgia"),
      {
        q: "Can you fence a large wooded lot in North Atlanta?",
        a: "Yes — multi-acre wooded lots are one of our sweet spots. We route around trees, follow natural contours, and minimize landscape disruption.",
      },
    ],
    lat: 33.7490,
    lng: -84.3880,
  },
  {
    slug: "charlotte-nc",
    city: "Charlotte",
    state: "NC",
    region: "Southeast",
    heroImage: galleryHound,
    metaTitle: "Fence Installation in Charlotte, NC | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across Greater Charlotte, NC. Designed for Piedmont clay and large suburban lots. Free consultation.",
    intro:
      "From SouthPark and Myers Park to Ballantyne, Waxhaw, and the lake communities, Greater Charlotte has a wide range of properties — and a wide range of fencing needs. Our nearly invisible systems work for compact yards and sprawling lake-house lots alike.",
    wildlifeNotes:
      "Suburban Mecklenburg and Union counties see growing deer and coyote pressure, especially in the wooded developments south of the city.",
    climateNotes:
      "Charlotte's hot, humid summers and mild winters are easier on fencing than the Northeast — but our hardware is still spec'd for long-term durability and UV exposure.",
    lotNotes:
      "From quarter-acre lots in established neighborhoods to multi-acre lake properties, we right-size every install.",
    whyChoose: [
      { title: "Piedmont clay expertise", description: "Posts properly engineered for North Carolina soil." },
      { title: "Lake-property capable", description: "Experienced with steeper, wooded lake-area lots." },
      { title: "Discreet & beautiful", description: "Nearly invisible — preserves the look of your landscaping." },
      { title: "Clear Charlotte scheduling", description: "Your Charlotte-area install date is confirmed in your written proposal." },
    ],
    nearbyAreas: ["Myers Park", "SouthPark", "Ballantyne", "Waxhaw", "Weddington", "Marvin", "Davidson", "Cornelius", "Huntersville", "Matthews"],
    localFaqs: [
      ...sharedFaqBase("Charlotte", "North Carolina"),
      {
        q: "Do you install on Lake Norman or Lake Wylie properties?",
        a: "Yes — we work throughout the Greater Charlotte lake communities, including Cornelius, Davidson, Huntersville, and the Lake Wylie side.",
      },
    ],
    lat: 35.2271,
    lng: -80.8431,
  },
  {
    slug: "raleigh-durham-nc",
    city: "Raleigh-Durham",
    state: "NC",
    region: "Southeast",
    heroImage: galleryDeerLandscape,
    metaTitle: "Fence Installation in Raleigh-Durham, NC | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across the Raleigh-Durham Triangle. Designed for wooded lots and Carolina deer pressure. Free consultation.",
    intro:
      "The Triangle — Raleigh, Durham, Chapel Hill, Cary, and surrounding towns — combines explosive growth with old wooded acreage. That mix puts pets, gardens, and landscape investments right in the middle of active deer corridors. Our fences solve it without dominating the view.",
    wildlifeNotes:
      "Triangle deer pressure is heavy in nearly every wooded subdivision. Coyote populations are also well-established across Wake, Durham, Orange, and Chatham counties.",
    climateNotes:
      "Hot summers, mild winters, occasional ice — our Southeast hardware spec handles it all without rust or sag.",
    lotNotes:
      "From Cary cul-de-sac lots to multi-acre Chapel Hill and Chatham County properties, we tailor every install.",
    whyChoose: [
      { title: "Triangle-tested", description: "Active across all the Triangle's growth corridors." },
      { title: "Wooded-lot specialists", description: "We follow the trees instead of clearing them." },
      { title: "Real garden protection", description: "Stops deer browsing on hostas, hydrangeas, and edibles." },
      { title: "Friendly, professional crews", description: "Local experience, respectful installs, fast turnaround." },
    ],
    nearbyAreas: ["Raleigh", "Durham", "Chapel Hill", "Cary", "Apex", "Hillsborough", "Pittsboro", "Holly Springs", "Wake Forest", "Morrisville"],
    localFaqs: [
      ...sharedFaqBase("Raleigh-Durham", "North Carolina"),
      {
        q: "How tall does my deer fence need to be in the Triangle?",
        a: "For most Triangle properties we recommend 7.5 to 8 feet of nearly invisible mesh — tall enough that deer simply won't attempt to jump it.",
      },
    ],
    lat: 35.7796,
    lng: -78.6382,
  },
  {
    slug: "nashville-tn",
    city: "Nashville",
    state: "TN",
    region: "Southeast",
    heroImage: galleryDeerHill,
    metaTitle: "Fence Installation in Nashville, TN | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across Greater Nashville. Designed for hill country, large lots, and Tennessee wildlife. Free consultation.",
    intro:
      "From Brentwood and Franklin to Belle Meade and the rolling hills of Williamson County, Greater Nashville has some of the most beautiful — and most deer-traveled — properties in the Southeast. Our fence systems disappear into hill-country landscapes while keeping pets safe and gardens whole.",
    wildlifeNotes:
      "Williamson and Davidson counties see heavy deer browsing, with coyote pressure layered on top — particularly in the wooded edges of Brentwood, Franklin, and Leiper's Fork.",
    climateNotes:
      "Hot, humid summers and mild winters with occasional ice. Our hardware is rated for the full Tennessee weather range.",
    lotNotes:
      "Many Nashville-area lots are sloped, wooded, or both. We're built for that terrain.",
    whyChoose: [
      { title: "Hill-country installs", description: "Comfortable on slopes and irregular lots." },
      { title: "Williamson County experience", description: "Active across Brentwood, Franklin, and surrounding areas." },
      { title: "Pet-safe by design", description: "A real physical barrier — not a shock collar." },
      { title: "Discreet finish", description: "Disappears into wooded backdrops." },
    ],
    nearbyAreas: ["Brentwood", "Franklin", "Belle Meade", "Forest Hills", "Nolensville", "Leiper's Fork", "Green Hills", "Oak Hill", "Thompson's Station"],
    localFaqs: [
      ...sharedFaqBase("Nashville", "Tennessee"),
      {
        q: "Can you install on the steep wooded lots common in Williamson County?",
        a: "Yes — sloped and wooded lots are one of our specialties. We follow the contour of the land instead of fighting it.",
      },
    ],
    lat: 36.1627,
    lng: -86.7816,
  },

  // ---------------- Northeast expansion ----------------
  {
    slug: "fairfield-county-ct",
    city: "Fairfield County",
    state: "CT",
    region: "Northeast",
    heroImage: galleryHound,
    metaTitle: "Fence Installation in Fairfield County, CT | Modern Fence Install",
    metaDescription:
      "Premium non-electric deer, dog, and garden fence installation across Fairfield County, CT — Greenwich, Darien, New Canaan, Westport. Free virtual consultation.",
    intro:
      "Fairfield County's Gold Coast — Greenwich, Darien, New Canaan, Westport, Wilton — combines some of the most valuable real estate on the East Coast with some of its heaviest deer pressure. Our nearly invisible fence systems protect six-figure landscape investments and keep family dogs safe, all without altering the architectural character that makes these towns what they are.",
    wildlifeNotes:
      "Lower Fairfield County is a national hot spot for deer-tick borne illness, driven by one of the densest deer populations in the country. Coyotes are now resident from the backcountry of Greenwich to the marshes of Westport, and a real physical fence is the only reliable answer.",
    climateNotes:
      "Coastal salt air in Old Greenwich and Southport, ice storms in the backcountry, and humid summers throughout — every component we install is galvanized and rated for the full Long Island Sound climate.",
    lotNotes:
      "From village-scale lots in downtown Darien to four-acre minimums in mid-country Greenwich, we engineer each install to the parcel, the wetlands setbacks, and any town-specific tree-protection rules.",
    whyChoose: [
      { title: "Estate-grade installations", description: "Trusted on properties from one-acre village lots to mid-country estates." },
      { title: "Wetlands & town-rules savvy", description: "We understand Greenwich, New Canaan, and Westport conservation overlays." },
      { title: "Discreet, professional crews", description: "Uniformed installers respectful of high-end properties and staff." },
      { title: "Real deer protection", description: "Engineered specifically for Lower Fairfield's intense deer pressure." },
    ],
    nearbyAreas: ["Greenwich", "Old Greenwich", "Riverside", "Cos Cob", "Darien", "New Canaan", "Westport", "Wilton", "Weston", "Easton", "Fairfield", "Southport"],
    localFaqs: [
      ...sharedFaqBase("Fairfield County", "Connecticut"),
      {
        q: "Will the fence work with Greenwich or New Canaan wetlands setbacks?",
        a: "Yes. Our system has minimal ground disturbance and a near-invisible profile, which makes it far easier to permit near regulated wetlands than traditional fencing. We'll review your specific setbacks during the consultation.",
      },
      {
        q: "Will it really keep ticks down by reducing deer in my yard?",
        a: "A properly sized deer fence is one of the most effective long-term tick-reduction strategies for a residential property. Removing deer from your yard breaks the lifecycle that drives Lyme exposure for your family and pets.",
      },
      {
        q: "Do you do deer fencing in Fairfield County?",
        a: "Deer fencing is the single most-requested service in Fairfield County and a core focus of our work here. We install 7.5 to 8-foot nearly invisible deer fencing across Greenwich, Darien, New Canaan, Westport, Wilton, and the rest of Lower Fairfield, sized to the brutal local deer pressure.",
      },
      {
        q: "How much does deer fencing cost in Fairfield County?",
        a: "Most Fairfield County deer fence projects land between $22 and $42 per linear foot installed, depending on terrain, gate count, and whether the run requires tree-protection setbacks. We provide a fixed written quote after the virtual consult — no surprise change orders.",
      },
    ],
    lat: 41.1408,
    lng: -73.2613,
  },
  {
    slug: "long-island-ny",
    city: "Long Island",
    state: "NY",
    region: "Northeast",
    heroImage: galleryDeerHill,
    metaTitle: "Fence Installation on Long Island, NY | Modern Fence Install",
    metaDescription:
      "Non-electric deer, dog, and garden fence installation across Long Island's North Shore and beyond — Nassau and Suffolk County. Free virtual consultation.",
    intro:
      "From Manhasset and Old Westbury on the North Shore to Huntington, Cold Spring Harbor, and the wooded estates of Lloyd Harbor, Long Island has both the deer pressure and the property values to make a real fence non-negotiable. Our nearly invisible systems protect dogs, gardens, and decades-old landscape plantings from the relentless browsing pressure that's reshaped Long Island gardening.",
    wildlifeNotes:
      "Long Island's North Shore is one of the most studied epicenters of tick-borne illness in the United States, driven by deer densities that have outpaced anything biologists planned for. A real physical deer fence is the single most effective intervention available to a homeowner.",
    climateNotes:
      "Salt spray, sandy soil, and full Atlantic coastal exposure — our hardware is selected specifically to handle the corrosion conditions unique to Long Island.",
    lotNotes:
      "Lots range from compact village parcels in Manhasset to multi-acre estates in Old Westbury and Lloyd Harbor. We engineer post depth and bracing for sandy, well-drained Long Island soils.",
    whyChoose: [
      { title: "Built for coastal corrosion", description: "Hardware spec'd for salt-air exposure across the North and South Shores." },
      { title: "Deer-density specialists", description: "Sized correctly for Long Island's exceptional deer pressure." },
      { title: "Nassau & Suffolk experience", description: "We know the permit and HOA differences across Long Island towns." },
      { title: "Discreet finish", description: "Designed to disappear from the road — preserves curb appeal." },
    ],
    nearbyAreas: ["Manhasset", "Sands Point", "Old Westbury", "Brookville", "Locust Valley", "Oyster Bay", "Cold Spring Harbor", "Huntington", "Lloyd Harbor", "Centre Island", "Mill Neck"],
    localFaqs: [
      ...sharedFaqBase("Long Island", "New York"),
      {
        q: "Do you install in both Nassau and Suffolk County?",
        a: "Yes — we cover the full North Shore corridor across both counties, from western Nassau through eastern Suffolk. Permit and HOA requirements differ between towns, and we'll walk you through what applies to your specific property.",
      },
      {
        q: "Will it hold up to Long Island salt air?",
        a: "Yes. Every component is galvanized and selected for coastal exposure. We've installed within a few hundred feet of the Sound and Atlantic without corrosion concerns.",
      },
    ],
    lat: 40.8259,
    lng: -73.4762,
  },
  {
    slug: "the-hamptons-ny",
    city: "The Hamptons",
    state: "NY",
    region: "Northeast",
    heroImage: galleryEstate,
    metaTitle: "Fence Installation in The Hamptons, NY | Modern Fence Install",
    metaDescription:
      "Premium non-electric deer, dog, and garden fence installation across the Hamptons — Southampton, East Hampton, Sag Harbor, Bridgehampton. Free consultation.",
    intro:
      "On the East End — Southampton, Bridgehampton, East Hampton, Sag Harbor, Wainscott, Amagansett — gardens are part of the architecture, and deer treat them as a buffet. Our nearly invisible deer fence systems protect six- and seven-figure landscape designs without imposing a visible barrier across the lawn or hedge line.",
    wildlifeNotes:
      "The East End has some of the most extreme deer densities in New York State, with year-round browsing pressure that destroys hostas, hydrangeas, roses, and vegetable gardens. A correctly sized deer fence is essentially the only durable solution.",
    climateNotes:
      "Atlantic salt spray, sandy soils, and harsh winter winds — our hardware and post system are engineered specifically for East End coastal conditions.",
    lotNotes:
      "From village lots behind privet in East Hampton to multi-acre oceanside estates in Sagaponack and Wainscott. We work closely with landscape architects to integrate fencing inside hedge rows so it disappears entirely.",
    whyChoose: [
      { title: "Landscape-architect friendly", description: "We routinely coordinate with East End landscape designers and estate managers." },
      { title: "Hidden inside hedges", description: "Designed to disappear behind privet and inside existing plantings." },
      { title: "Coastal-rated hardware", description: "Built for sandy soil, salt spray, and Atlantic winter wind." },
      { title: "Discreet, off-season ready", description: "Quiet installs that respect the rhythm of an estate." },
    ],
    nearbyAreas: ["Southampton", "Bridgehampton", "East Hampton", "Sag Harbor", "Sagaponack", "Wainscott", "Amagansett", "Water Mill", "Quogue", "North Haven", "Shelter Island"],
    localFaqs: [
      ...sharedFaqBase("The Hamptons", "New York"),
      {
        q: "Can the fence be installed inside an existing privet hedge?",
        a: "Yes — that's one of the most popular installation patterns we do on the East End. The fence sits inside the hedge line, completely hidden from view, with the hedge providing the visual screen and the fence doing the actual deer work.",
      },
      {
        q: "Do you coordinate with landscape architects and estate managers?",
        a: "Routinely. Most East End projects involve a landscape architect, a property manager, or both, and we're set up to work inside that team.",
      },
    ],
    lat: 40.9176,
    lng: -72.3857,
  },
  {
    slug: "princeton-nj",
    city: "Princeton",
    state: "NJ",
    region: "Northeast",
    heroImage: galleryDeerLandscape,
    metaTitle: "Fence Installation in Princeton, NJ | Modern Fence Install",
    metaDescription:
      "Non-electric deer, dog, and garden fence installation across Princeton and central New Jersey — Hopewell, Pennington, Lawrenceville, Hunterdon. Free consultation.",
    intro:
      "Princeton and the surrounding Mercer and Hunterdon countryside — Hopewell, Pennington, Lawrenceville, Lambertville — combine historic character with serious deer pressure and a strong equestrian and gardening culture. Our nearly invisible fence systems protect mature landscaping and pets while staying respectful of the area's architectural heritage.",
    wildlifeNotes:
      "Central New Jersey deer densities are among the highest in the state, with year-round browsing that wipes out everything from tulip bulbs to mature shrub plantings. Coyote presence has grown across Hunterdon and western Mercer as well.",
    climateNotes:
      "Humid summers, ice and freeze-thaw winters, and clay-heavy soils — our hardware spec and post-setting depth account for all three.",
    lotNotes:
      "Lots range from in-town Princeton parcels to ten-acre horse properties in Hopewell and Hunterdon. We work comfortably across that full range, including around paddocks and pasture lines.",
    whyChoose: [
      { title: "Equestrian-property experience", description: "Comfortable working around paddocks, pastures, and run-in sheds." },
      { title: "Historic-area discretion", description: "Low-visual-impact design that fits Princeton's architectural character." },
      { title: "Real deer protection", description: "Sized for Mercer and Hunterdon's exceptional browsing pressure." },
      { title: "Wooded-lot specialists", description: "We follow tree lines instead of clearing them." },
    ],
    nearbyAreas: ["Princeton", "Hopewell", "Pennington", "Lawrenceville", "Lambertville", "Stockton", "Ringoes", "Flemington", "Skillman", "Belle Mead"],
    localFaqs: [
      ...sharedFaqBase("Princeton", "New Jersey"),
      {
        q: "Can you fence around a horse paddock or pasture?",
        a: "Yes. We frequently work alongside existing equestrian fencing — adding deer protection around gardens, around the home perimeter, or around hay storage and feed areas.",
      },
      {
        q: "Will it really stop the deer browsing in my Princeton garden?",
        a: "Yes — that's the core use case here. A correctly sized 7.5- to 8-foot deer fence is the only durable, non-chemical answer to the browsing pressure across Mercer and Hunterdon counties.",
      },
    ],
    lat: 40.3573,
    lng: -74.6672,
  },

  // ---------------- Mid-Atlantic expansion ----------------
  {
    slug: "loudoun-county-va",
    city: "Loudoun County",
    state: "VA",
    region: "Mid-Atlantic",
    heroImage: galleryDeerHill,
    metaTitle: "Fence Installation in Loudoun County, VA | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across Loudoun County, VA — Leesburg, Ashburn, Middleburg, Purcellville. Free virtual consultation.",
    intro:
      "Loudoun County stretches from the dense HOA neighborhoods of Ashburn and Brambleton out to the horse country of Middleburg, Upperville, and Purcellville. That spectrum demands two very different fencing approaches — and we do both, from quarter-acre suburban dog containment to multi-acre deer protection on horse farms along the Potomac.",
    wildlifeNotes:
      "Western Loudoun sits on a major deer corridor running south from the Potomac and east out of the Blue Ridge foothills. Coyotes are well-established across the county, and bear sightings now reach as far east as Purcellville.",
    climateNotes:
      "Northern Virginia clay soils, ice storms, humid summers, and the freeze-thaw cycle along the Potomac all factor into our post-setting depth and hardware spec.",
    lotNotes:
      "From sub-quarter-acre Ashburn lots to ten- and twenty-acre Middleburg estates. We engineer each install to the parcel, the HOA design rules (where applicable), and the terrain.",
    whyChoose: [
      { title: "HOA navigation", description: "Active in dozens of Ashburn, Brambleton, and Lansdowne associations." },
      { title: "Horse-farm capable", description: "Comfortable working alongside existing equestrian fencing in western Loudoun." },
      { title: "Built for clay & freeze-thaw", description: "Posts engineered specifically for Northern Virginia soil conditions." },
      { title: "Real deer protection", description: "Sized for the corridor pressure off the Blue Ridge and Potomac." },
    ],
    nearbyAreas: ["Leesburg", "Ashburn", "Brambleton", "Lansdowne", "Sterling", "Purcellville", "Round Hill", "Hamilton", "Middleburg", "Upperville", "Aldie", "Waterford"],
    localFaqs: [
      ...sharedFaqBase("Loudoun County", "Virginia"),
      {
        q: "Will it pass my Ashburn or Brambleton HOA?",
        a: "Almost always, yes. The near-invisible profile of our system is far more likely to clear ARB review than a traditional tall perimeter fence. We provide spec sheets and renderings to support the application.",
      },
      {
        q: "Do you install on horse farms in Middleburg or Upperville?",
        a: "Yes. We routinely add deer fencing around gardens, home perimeters, and pasture-edge plantings on western Loudoun horse properties without disturbing existing post-and-rail.",
      },
    ],
    lat: 39.0857,
    lng: -77.6452,
  },

  // ---------------- Southeast expansion ----------------
  {
    slug: "chapel-hill-nc",
    city: "Chapel Hill",
    state: "NC",
    region: "Southeast",
    heroImage: galleryHound,
    metaTitle: "Fence Installation in Chapel Hill, NC | Modern Fence Install",
    metaDescription:
      "Non-electric deer, dog, and garden fence installation across Chapel Hill, Carrboro, and Orange County, NC. Designed for wooded lots. Free consultation.",
    intro:
      "Chapel Hill, Carrboro, and the wooded neighborhoods of Orange and Chatham counties combine mature canopy, heavy deer pressure, and a serious gardening culture. Our nearly invisible fence systems protect ornamental beds, vegetable gardens, and family pets without clearing the trees that make these properties special.",
    wildlifeNotes:
      "Orange and Chatham county deer browsing is intense in nearly every wooded subdivision, and coyote populations have grown notably over the last decade. Real physical fencing is the only durable answer for serious gardeners here.",
    climateNotes:
      "Hot, humid summers, mild winters with occasional ice events, and red-clay soils common across the Piedmont — our hardware and post depth are spec'd for all of it.",
    lotNotes:
      "Lots range from in-town Chapel Hill parcels to multi-acre wooded properties in Chatham County. We follow contours and route around mature trees rather than clearing them.",
    whyChoose: [
      { title: "Wooded-lot specialists", description: "We work with the trees instead of against them." },
      { title: "Garden-protection focus", description: "Stops deer browsing on hostas, hydrangeas, and edible beds." },
      { title: "Piedmont clay expertise", description: "Posts properly engineered for Orange County soils." },
      { title: "Discreet finish", description: "Disappears into wooded backdrops." },
    ],
    nearbyAreas: ["Chapel Hill", "Carrboro", "Hillsborough", "Pittsboro", "Fearrington Village", "Governors Club", "Briar Chapel", "Southern Village", "Meadowmont"],
    localFaqs: [
      ...sharedFaqBase("Chapel Hill", "North Carolina"),
      {
        q: "Will it really stop deer browsing in my Chapel Hill garden?",
        a: "Yes. A correctly sized 7.5- to 8-foot deer fence is the only durable answer to Orange County's browsing pressure — repellents and plant choice only get you so far.",
      },
      {
        q: "Do you work in Chatham County (Pittsboro, Fearrington, Briar Chapel)?",
        a: "Yes — we cover the full Chapel Hill and southern Orange / northern Chatham corridor.",
      },
    ],
    lat: 35.9132,
    lng: -79.0558,
  },
  {
    slug: "greenville-sc",
    city: "Greenville",
    state: "SC",
    region: "Southeast",
    heroImage: gardenFenceReal,
    metaTitle: "Fence Installation in Greenville, SC | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across Greater Greenville, SC — Cliffs communities, Travelers Rest, Simpsonville. Free consultation.",
    intro:
      "Greater Greenville — from downtown's Augusta Road and North Main neighborhoods to the Cliffs communities along the Blue Ridge foothills — has become one of the Southeast's fastest-growing premium markets. Our nearly invisible fence systems fit the region's mix of in-town gardens and large foothill properties without disrupting the look that draws people here.",
    wildlifeNotes:
      "Northern Greenville County and the foothills see steady deer pressure, and coyote populations are well-established countywide. Bear sightings continue to grow closer to the Blue Ridge escarpment.",
    climateNotes:
      "Hot, humid summers, mild winters, intense UV, and occasional ice — our hardware is selected for the long-term in this Southeast climate.",
    lotNotes:
      "From quarter-acre in-town lots in downtown Greenville to multi-acre foothill properties in Travelers Rest and the Cliffs. We engineer for slope, soil, and tree cover.",
    whyChoose: [
      { title: "Foothill-property capable", description: "Comfortable on the sloped, wooded lots common north of the city." },
      { title: "UV- and humidity-rated", description: "Hardware selected for the Southeast climate." },
      { title: "In-town garden focus", description: "Discreet protection for downtown Greenville's premium gardens." },
      { title: "Clear scheduling", description: "Your Greenville-area install date is confirmed in your written proposal." },
    ],
    nearbyAreas: ["Downtown Greenville", "Augusta Road", "North Main", "Travelers Rest", "Cliffs at Glassy", "Cliffs Valley", "Simpsonville", "Mauldin", "Greer", "Easley"],
    localFaqs: [
      ...sharedFaqBase("Greenville", "South Carolina"),
      {
        q: "Do you install at the Cliffs communities or other foothill properties?",
        a: "Yes — sloped, wooded foothill lots are one of our specialties. We follow the contour of the land instead of fighting it.",
      },
      {
        q: "Can you protect a downtown Greenville urban garden?",
        a: "Absolutely. The same nearly invisible system scales down beautifully for in-town gardens on Augusta Road or North Main.",
      },
    ],
    lat: 34.8526,
    lng: -82.3940,
  },
  {
    slug: "asheville-nc",
    city: "Asheville",
    state: "NC",
    region: "Southeast",
    heroImage: galleryGermanShepherd,
    metaTitle: "Fence Installation in Asheville, NC | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across Asheville and the Western North Carolina mountains. Built for slope and bear country. Free consultation.",
    intro:
      "Asheville and the surrounding mountain communities — Biltmore Forest, Black Mountain, Weaverville, Hendersonville — combine some of the most dramatic terrain in the Southeast with serious wildlife pressure. Our nearly invisible fence systems protect mountain gardens, contain dogs on steep lots, and minimize landscape disruption on the slopes that define this region.",
    wildlifeNotes:
      "Western North Carolina has heavy deer pressure across nearly every wooded neighborhood, and black bear visits to gardens, beehives, and chicken coops are routine at Asheville-area elevations. A real physical fence is the only durable wildlife answer here.",
    climateNotes:
      "Mountain freeze-thaw, ice events, and intense summer thunderstorms — our hardware and post-setting accounts for the hardware stress that mountain weather creates.",
    lotNotes:
      "Most Asheville-area lots are sloped, wooded, or both. We're built for that terrain and routinely install on grades that defeat traditional fencing crews.",
    whyChoose: [
      { title: "Mountain-slope installs", description: "Comfortable on the grades typical of Asheville-area lots." },
      { title: "Bear-aware design", description: "Garden-perimeter and coop-protection layouts built for WNC realities." },
      { title: "Freeze-thaw rated", description: "Hardware selected for the mountain weather cycle." },
      { title: "Discreet in wooded settings", description: "Disappears against a forest backdrop." },
    ],
    nearbyAreas: ["Biltmore Forest", "Montford", "North Asheville", "Black Mountain", "Weaverville", "Fairview", "Arden", "Fletcher", "Hendersonville", "Mills River"],
    localFaqs: [
      ...sharedFaqBase("Asheville", "North Carolina"),
      {
        q: "Can you install on the steep mountain lots common around Asheville?",
        a: "Yes — sloped, wooded mountain lots are one of our specialties. We follow the contour of the land and minimize regrading.",
      },
      {
        q: "Will the fence help with bear visits to my garden or chicken coop?",
        a: "Our standard system is designed primarily for deer and dogs. For bear-pressured properties we'll discuss layout choices — height, layered perimeter, and gate hardware — that meaningfully reduce easy access. We'll be straightforward about what a non-electric fence will and won't deter a determined bear.",
      },
    ],
    lat: 35.5951,
    lng: -82.5515,
  },

  // ---------------- New: County / suburb expansion (CT, NY, MA, RI, VT, NH, PA) ----------------
  {
    slug: "litchfield-county-ct",
    city: "Litchfield County",
    state: "CT",
    region: "Northeast",
    heroImage: galleryDeerLandscape,
    metaTitle: "Fence Installation in Litchfield County, CT — Washington, Roxbury, Kent | Modern Fence Install",
    metaDescription:
      "Premium non-electric deer, dog, and garden fence installation across Litchfield County, CT — Washington, Roxbury, Kent, Salisbury, Litchfield. Free consultation.",
    intro:
      "Litchfield County is Connecticut's quiet back-country — Washington, Roxbury, Kent, Salisbury, Sharon, Cornwall — full of weekend estates, working farms, and the deer pressure that comes with rural Connecticut. Our nearly invisible fence systems protect six-figure landscape investments and equestrian properties without disrupting the views that bring people here.",
    wildlifeNotes:
      "The Northwest Hills carry some of the highest deer densities in Connecticut, plus year-round bear activity, coyotes, and bobcats. A real physical perimeter is essential for gardens, dogs, and livestock.",
    climateNotes:
      "Hilltown winters are colder and snowier than the Connecticut shore. Our hardware is rated for the deeper snow loads and longer freeze cycles of the Northwest Hills.",
    lotNotes:
      "Litchfield County properties commonly run from 5 to 50+ acres of pasture, woodland, and stone walls. We engineer for slope, ledge, and the long perimeters typical of country properties.",
    whyChoose: [
      { title: "Estate & farm-scale capable", description: "Comfortable on multi-acre rural properties with long perimeters." },
      { title: "Wildlife-effective", description: "Sized for the heavy deer and bear pressure of the Northwest Hills." },
      { title: "Discreet from the road", description: "Preserves the rural character that defines Litchfield County." },
      { title: "Equestrian-friendly", description: "Layouts that work alongside paddocks, riding rings, and barns." },
    ],
    nearbyAreas: ["Washington", "Roxbury", "Kent", "Salisbury", "Sharon", "Cornwall", "Litchfield", "New Preston", "Bridgewater", "Warren", "Goshen", "Morris"],
    localFaqs: [
      ...sharedFaqBase("Litchfield County", "Connecticut"),
      {
        q: "Can you fence a 10+ acre Litchfield County property?",
        a: "Absolutely — large rural properties are one of our specialties. We efficiently install long perimeters and adapt to ledge, wetlands, and pasture boundaries.",
      },
    ],
    lat: 41.7926,
    lng: -73.2123,
  },
  {
    slug: "new-haven-county-suburbs-ct",
    city: "New Haven County Shoreline",
    state: "CT",
    region: "Northeast",
    heroImage: hiddenFence,
    metaTitle: "Fence Installation in New Haven County Shoreline — Madison, Guilford, Branford | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across the Connecticut shoreline suburbs — Madison, Guilford, Branford, Woodbridge. Free consultation.",
    intro:
      "We serve the shoreline and inland suburbs of New Haven County — Madison, Guilford, Branford, Woodbridge, Bethany, Orange — where one-acre-plus lots are common. These coastal towns combine strong deer pressure, gorgeous garden investment, and the kind of suburban lots that demand a real, durable fence.",
    wildlifeNotes:
      "Coastal Connecticut has heavy deer browsing year-round and an established coyote population. The shoreline is also one of the highest-Lyme-incidence regions in the country, which is why deer-exclusion fencing has become so popular here.",
    climateNotes:
      "Salt air on the shoreline and full New England weather inland — every component is galvanized and selected for the corrosion conditions of Long Island Sound.",
    lotNotes:
      "From half-acre village lots in central Madison to wooded multi-acre properties in Woodbridge, we engineer each install to the parcel and any wetlands setbacks.",
    whyChoose: [
      { title: "Coastal-corrosion rated", description: "Hardware spec'd for salt-air exposure on the Connecticut shoreline." },
      { title: "Lyme-reduction strategy", description: "A correctly sized deer fence is a proven long-term tick intervention." },
      { title: "Suburban focus", description: "We specialize in the shoreline and inland towns of New Haven County." },
      { title: "Discreet finish", description: "Designed to disappear from the road and preserve curb appeal." },
    ],
    nearbyAreas: ["Madison", "Guilford", "Branford", "Woodbridge", "Bethany", "Orange", "North Branford", "Killingworth", "Clinton", "Westbrook"],
    localFaqs: [
      ...sharedFaqBase("the New Haven County shoreline", "Connecticut"),
      {
        q: "Which New Haven County towns do you serve?",
        a: "We specialize in the shoreline and inland suburbs of New Haven County — Madison, Guilford, Branford, Woodbridge, Bethany, Orange, and surrounding towns — where homeowners typically have an acre or more.",
      },
    ],
    lat: 41.2787,
    lng: -72.6018,
  },
  {
    slug: "hudson-valley-ny",
    city: "Hudson Valley",
    state: "NY",
    region: "Northeast",
    heroImage: galleryEstate,
    metaTitle: "Fence Installation in the Hudson Valley, NY — Dutchess, Putnam, Columbia | Modern Fence Install",
    metaDescription:
      "Premium non-electric deer, dog, and garden fence installation across the Hudson Valley — Millbrook, Rhinebeck, Cold Spring, Garrison. Free consultation.",
    intro:
      "The Hudson Valley — Millbrook, Rhinebeck, Red Hook, Cold Spring, Garrison, Pawling — is built around weekend estates, working horse farms, and serious garden investment, all set against some of the highest deer densities in New York. Our nearly invisible fence systems protect what brings you here without imposing a visible barrier across the view.",
    wildlifeNotes:
      "Dutchess and Putnam counties have brutal deer pressure year-round, with growing bear, coyote, and fisher activity. A real physical fence is the only durable answer for orchards, ornamentals, and dogs.",
    climateNotes:
      "Hudson Valley winters bring deep cold and significant snow. Hardware and post-setting are spec'd for the freeze-thaw cycles typical of the region.",
    lotNotes:
      "Properties commonly run from 3 to 50+ acres of meadow, woodland, and pasture. We're built for long perimeters and irregular country boundaries.",
    whyChoose: [
      { title: "Estate-scale capable", description: "Comfortable on multi-acre properties typical of the Hudson Valley." },
      { title: "Equestrian-friendly", description: "Works alongside paddocks, riding rings, and barn complexes." },
      { title: "Real deer protection", description: "Sized for the intense pressure of Dutchess and Putnam counties." },
      { title: "Discreet by design", description: "Disappears against meadow and woodland backdrops." },
    ],
    nearbyAreas: ["Millbrook", "Rhinebeck", "Red Hook", "Pawling", "Cold Spring", "Garrison", "Pine Plains", "Stanfordville", "Amenia", "Salt Point"],
    localFaqs: [
      ...sharedFaqBase("the Hudson Valley", "New York"),
      {
        q: "Can you fence a horse farm in Dutchess or Putnam County?",
        a: "Yes — equestrian properties are a core part of our work in the Hudson Valley. We design layouts that work with paddock fencing, gates, and existing infrastructure.",
      },
    ],
    lat: 41.7858,
    lng: -73.7002,
  },
  {
    slug: "saratoga-county-ny",
    city: "Saratoga County",
    state: "NY",
    region: "Northeast",
    heroImage: galleryHound,
    metaTitle: "Fence Installation in Saratoga County, NY — Saratoga Springs, Wilton, Greenfield | Modern Fence Install",
    metaDescription:
      "Non-electric deer, dog, and garden fence installation across Saratoga County, NY — Saratoga Springs, Wilton, Greenfield, Malta. Free consultation.",
    intro:
      "Saratoga County combines the racing-country estates of Saratoga Springs with growing suburbs in Wilton, Malta, Greenfield, and Ballston. The deer pressure is heavy, the lots are generous, and homeowners are increasingly looking for a fence that protects gardens and pets without blocking the country view.",
    wildlifeNotes:
      "Upstate deer densities are high year-round, with established coyote and bear populations across the county. Our deer fences are sized to the local pressure.",
    climateNotes:
      "Upstate New York winters bring deep snow and prolonged freeze cycles. Our hardware is engineered for these conditions.",
    lotNotes:
      "From in-town lots in Saratoga Springs to multi-acre properties in the surrounding towns, we right-size every install.",
    whyChoose: [
      { title: "Upstate weather-rated", description: "Hardware built for deep snow and long freeze cycles." },
      { title: "Equestrian-area experience", description: "Comfortable around the horse properties common throughout the county." },
      { title: "Real deer protection", description: "Sized for the heavy pressure across Saratoga County." },
      { title: "Discreet finish", description: "Preserves the open country views that define this region." },
    ],
    nearbyAreas: ["Saratoga Springs", "Wilton", "Greenfield", "Malta", "Ballston Spa", "Saratoga", "Stillwater", "Charlton", "Galway"],
    localFaqs: [
      ...sharedFaqBase("Saratoga County", "New York"),
      {
        q: "Will the fence stand up to upstate winters?",
        a: "Yes. Every component is selected and installed for the deep snow loads and freeze-thaw cycles typical of upstate New York.",
      },
    ],
    lat: 43.0831,
    lng: -73.7846,
  },
  {
    slug: "columbia-county-ny",
    city: "Columbia County",
    state: "NY",
    region: "Northeast",
    heroImage: galleryDeerHill,
    metaTitle: "Fence Installation in Columbia County, NY — Hudson, Chatham, Kinderhook | Modern Fence Install",
    metaDescription:
      "Non-electric deer, dog, and garden fence installation across Columbia County, NY — Hudson, Chatham, Kinderhook, Ghent. Free consultation.",
    intro:
      "Columbia County — Hudson, Chatham, Kinderhook, Ghent, Hillsdale — has become one of the Northeast's premier weekend-and-second-home regions, with serious garden investment to match. Our nearly invisible fence systems protect orchards, ornamentals, and family pets across the county's open country and wooded edges.",
    wildlifeNotes:
      "Columbia County deer pressure is heavy year-round, plus active bear, coyote, and fisher populations. A correctly sized fence is essential for any meaningful garden or orchard.",
    climateNotes:
      "Cold upstate winters with significant snow loads — our hardware and post-setting handle the freeze-thaw cycles.",
    lotNotes:
      "Properties commonly run from 3 to 100+ acres of farmland, meadow, and woodland. We're efficient on long rural perimeters.",
    whyChoose: [
      { title: "Country-property scale", description: "Comfortable on the multi-acre weekend and farm properties typical of Columbia County." },
      { title: "Orchard & garden protection", description: "Sized correctly for the deer and bear pressure that destroys upstate orchards." },
      { title: "Discreet from the road", description: "Disappears against meadow and woodland backdrops." },
      { title: "Long-perimeter capable", description: "Built for the rural property scale of the Hudson Valley region." },
    ],
    nearbyAreas: ["Hudson", "Chatham", "Kinderhook", "Ghent", "Hillsdale", "Claverack", "Ancram", "Copake", "Livingston", "Old Chatham"],
    localFaqs: [
      ...sharedFaqBase("Columbia County", "New York"),
      {
        q: "Can the fence protect an orchard or vegetable plot from bears?",
        a: "Our standard system is sized primarily for deer. For bear-pressured properties we discuss layered perimeter and gate hardware that meaningfully reduce easy access. We'll be straightforward about what a non-electric fence will and won't deter.",
      },
    ],
    lat: 42.2531,
    lng: -73.6618,
  },
  {
    slug: "the-berkshires-ma",
    city: "The Berkshires",
    state: "MA",
    region: "Northeast",
    heroImage: galleryDeerLandscape,
    metaTitle: "Fence Installation in the Berkshires, MA — Stockbridge, Lenox, Great Barrington | Modern Fence Install",
    metaDescription:
      "Premium non-electric deer, dog, and garden fence installation across the Berkshires — Stockbridge, Lenox, Great Barrington, Williamstown. Free consultation.",
    intro:
      "Western Massachusetts — Stockbridge, Lenox, Great Barrington, Williamstown, Lee, Sheffield — combines country estates, weekend homes, and serious deer pressure. Our nearly invisible fence systems protect the gardens and properties that define the Berkshires without imposing a visible perimeter on the landscape.",
    wildlifeNotes:
      "The Berkshires carry heavy deer densities and active bear, coyote, and bobcat populations. A real physical fence is essential for any serious garden or orchard.",
    climateNotes:
      "Berkshires winters are colder and snowier than coastal Massachusetts. Our hardware is rated for the deep snow loads and freeze cycles.",
    lotNotes:
      "Properties typically run from 2 to 50+ acres of meadow, woodland, and pasture. We're built for the irregular country boundaries common here.",
    whyChoose: [
      { title: "Country-estate capable", description: "Comfortable on the multi-acre weekend properties typical of the Berkshires." },
      { title: "Cold-climate rated", description: "Hardware built for Western Massachusetts winters." },
      { title: "Orchard & garden protection", description: "Sized for the heavy deer and bear pressure of the Berkshires." },
      { title: "Discreet finish", description: "Disappears against the woodland and meadow backdrops that define the region." },
    ],
    nearbyAreas: ["Stockbridge", "Lenox", "Great Barrington", "Williamstown", "Lee", "Sheffield", "Egremont", "Tyringham", "Richmond", "West Stockbridge"],
    localFaqs: [
      ...sharedFaqBase("the Berkshires", "Massachusetts"),
      {
        q: "Do you install on country properties throughout the Berkshires?",
        a: "Yes — we cover the full Berkshires region from Williamstown south to Sheffield, and we're built for the multi-acre rural properties typical of Western Mass.",
      },
    ],
    lat: 42.3520,
    lng: -73.3115,
  },
  {
    slug: "cape-cod-ma",
    city: "Cape Cod",
    state: "MA",
    region: "Northeast",
    heroImage: deerFenceReal,
    metaTitle: "Fence Installation on Cape Cod, MA — Chatham, Wellfleet, Brewster | Modern Fence Install",
    metaDescription:
      "Non-electric deer, dog, and garden fence installation across Cape Cod — Chatham, Wellfleet, Brewster, Orleans, Truro. Free consultation.",
    intro:
      "Cape Cod's outer towns — Chatham, Wellfleet, Brewster, Orleans, Truro, Eastham — combine serious deer browsing with sandy soil, salt air, and the kind of garden investment that defines a Cape summer house. Our nearly invisible fence systems are engineered specifically for these coastal conditions.",
    wildlifeNotes:
      "Cape Cod deer pressure is intense year-round, particularly outside the peak summer season. Our deer fences are sized to keep gardens whole through fall and winter.",
    climateNotes:
      "Salt spray, sandy soil, and full Atlantic exposure — every component is selected for the corrosion conditions unique to the Cape.",
    lotNotes:
      "Properties range from compact village lots to multi-acre wooded parcels. We engineer post depth and bracing for sandy Cape soils.",
    whyChoose: [
      { title: "Coastal-corrosion rated", description: "Hardware spec'd for salt-air exposure across the Outer Cape." },
      { title: "Sandy-soil expertise", description: "Posts engineered and set for Cape Cod soil conditions." },
      { title: "Garden protection that works", description: "Sized for the year-round deer browsing pressure that destroys Cape gardens." },
      { title: "Discreet finish", description: "Preserves the cottage character that defines the Outer Cape." },
    ],
    nearbyAreas: ["Chatham", "Wellfleet", "Brewster", "Orleans", "Truro", "Eastham", "Dennis", "Harwich", "Yarmouth Port"],
    localFaqs: [
      ...sharedFaqBase("Cape Cod", "Massachusetts"),
      {
        q: "Will the fence hold up to Cape Cod salt air?",
        a: "Yes. Every component is galvanized and selected for coastal exposure. We've installed within a few hundred feet of the Atlantic without corrosion concerns.",
      },
    ],
    lat: 41.7003,
    lng: -70.0301,
  },
  {
    slug: "newport-county-ri",
    city: "Newport County",
    state: "RI",
    region: "Northeast",
    heroImage: galleryEstate,
    metaTitle: "Fence Installation in Newport County, RI — Newport, Middletown, Portsmouth | Modern Fence Install",
    metaDescription:
      "Premium non-electric deer, dog, and garden fence installation across Newport County, RI — Newport, Middletown, Portsmouth, Jamestown, Little Compton. Free consultation.",
    intro:
      "Newport County — Newport, Middletown, Portsmouth, Jamestown, Little Compton, Tiverton — combines historic estates, working farms, and some of the most photographed gardens in New England. The deer pressure is intense, and our nearly invisible fence systems protect six- and seven-figure landscape investments without imposing a visible barrier on the property.",
    wildlifeNotes:
      "Aquidneck Island and the surrounding mainland carry extraordinary deer densities for their size, plus growing coyote pressure. A real physical fence is the only durable answer.",
    climateNotes:
      "Salt air, ocean exposure, and full New England weather — every component is galvanized and selected for the corrosion conditions of Narragansett Bay.",
    lotNotes:
      "Properties range from compact village lots in downtown Newport to multi-acre estates in Portsmouth, Middletown, and Little Compton. We engineer for the parcel and any historic-district setbacks.",
    whyChoose: [
      { title: "Estate-grade installations", description: "Trusted on properties from village lots to multi-acre coastal estates." },
      { title: "Coastal-corrosion rated", description: "Hardware built for the salt air of Narragansett Bay." },
      { title: "Historic-district friendly", description: "Low-profile design that respects Newport's architectural review process." },
      { title: "Real deer protection", description: "Sized for the heavy deer pressure across Aquidneck Island." },
    ],
    nearbyAreas: ["Newport", "Middletown", "Portsmouth", "Jamestown", "Little Compton", "Tiverton"],
    localFaqs: [
      ...sharedFaqBase("Newport County", "Rhode Island"),
      {
        q: "Will my historic Newport property qualify?",
        a: "In most cases yes — our nearly invisible profile is far more likely to clear historic-district review than traditional 6 or 8-foot fencing.",
      },
    ],
    lat: 41.4901,
    lng: -71.3128,
  },
  {
    slug: "washington-county-ri",
    city: "Washington County",
    state: "RI",
    region: "Northeast",
    heroImage: galleryDeerLandscape,
    metaTitle: "Fence Installation in South County RI — Watch Hill, Westerly, Charlestown | Modern Fence Install",
    metaDescription:
      "Non-electric deer, dog, and garden fence installation across South County, RI — Watch Hill, Westerly, Charlestown, Narragansett, South Kingstown. Free consultation.",
    intro:
      "South County — Watch Hill, Westerly, Charlestown, Narragansett, South Kingstown, Wakefield — combines coastal estates, family compounds, and the kind of garden investment that defines a Rhode Island summer property. Our nearly invisible fence systems are engineered for the salt air, sandy soil, and serious deer pressure of the southern Rhode Island shoreline.",
    wildlifeNotes:
      "South County deer densities are high year-round, with growing coyote pressure across Washington County. Our deer fences are sized to keep gardens and pets safe through every season.",
    climateNotes:
      "Salt spray, sandy soil, and full Atlantic exposure — hardware selected specifically for these coastal conditions.",
    lotNotes:
      "Properties range from compact village lots in Watch Hill to multi-acre wooded parcels inland. We engineer post depth and bracing for sandy coastal soils.",
    whyChoose: [
      { title: "Coastal-corrosion rated", description: "Hardware spec'd for the salt air of the South County shore." },
      { title: "Sandy-soil expertise", description: "Posts engineered for southern Rhode Island soil conditions." },
      { title: "Family-compound capable", description: "Comfortable on multi-acre coastal properties." },
      { title: "Discreet finish", description: "Preserves the shoreline character that defines South County." },
    ],
    nearbyAreas: ["Watch Hill", "Westerly", "Charlestown", "Narragansett", "South Kingstown", "Wakefield", "Hopkinton", "Richmond"],
    localFaqs: [
      ...sharedFaqBase("South County", "Rhode Island"),
      {
        q: "Will the fence hold up so close to the ocean?",
        a: "Yes. We've installed within a few hundred feet of the Atlantic without corrosion concerns — every component is galvanized and selected for coastal exposure.",
      },
    ],
    lat: 41.4264,
    lng: -71.7642,
  },
  {
    slug: "southern-vermont",
    city: "Southern Vermont",
    state: "VT",
    region: "Northeast",
    heroImage: galleryDeerHill,
    metaTitle: "Fence Installation in Southern Vermont — Manchester, Stratton, Dorset, Woodstock | Modern Fence Install",
    metaDescription:
      "Premium non-electric deer, dog, and garden fence installation across Southern Vermont — Manchester, Stratton, Dorset, Woodstock, Weston. Free consultation.",
    intro:
      "Southern Vermont — Manchester, Dorset, Stratton, Woodstock, Weston, Londonderry — is built around country estates, ski-house second homes, and working farms. The deer and bear pressure is significant, and our nearly invisible fence systems protect gardens, orchards, and family pets without disrupting the views that define the region.",
    wildlifeNotes:
      "Southern Vermont carries heavy deer pressure plus very active bear, coyote, and fisher populations. A real physical fence is essential for any serious garden or orchard.",
    climateNotes:
      "Vermont winters are deep and long. Our hardware and post-setting are spec'd for the snow loads and prolonged freeze cycles typical of the Green Mountains.",
    lotNotes:
      "Properties commonly run from 5 to 100+ acres of meadow, woodland, and pasture. We're built for long rural perimeters and irregular country boundaries.",
    whyChoose: [
      { title: "Country-estate capable", description: "Comfortable on multi-acre Vermont properties." },
      { title: "Deep-winter rated", description: "Hardware engineered for the snow loads and freeze cycles of the Green Mountains." },
      { title: "Orchard & garden protection", description: "Sized for the heavy deer and bear pressure of Southern Vermont." },
      { title: "Equestrian-friendly", description: "Layouts that work alongside paddocks and pasture." },
    ],
    nearbyAreas: ["Manchester", "Dorset", "Stratton", "Woodstock", "Weston", "Londonderry", "Pawlet", "Arlington", "Quechee", "Killington"],
    localFaqs: [
      ...sharedFaqBase("Southern Vermont", "Vermont"),
      {
        q: "Can you fence a Vermont country property with serious bear activity?",
        a: "Our standard system is sized primarily for deer. For bear-pressured properties we discuss layered perimeter and gate hardware that meaningfully reduce easy access. We'll be straightforward about what a non-electric fence will and won't deter.",
      },
    ],
    lat: 43.1631,
    lng: -73.0723,
  },
  {
    slug: "seacoast-nh",
    city: "Seacoast New Hampshire",
    state: "NH",
    region: "Northeast",
    heroImage: galleryHound,
    metaTitle: "Fence Installation on the NH Seacoast — Rye, New Castle, Stratham, Hampton | Modern Fence Install",
    metaDescription:
      "Non-electric deer, dog, and garden fence installation across the New Hampshire Seacoast — Rye, New Castle, Stratham, Hampton, North Hampton. Free consultation.",
    intro:
      "The New Hampshire Seacoast — Rye, New Castle, Stratham, Hampton, North Hampton, Greenland — combines coastal suburbs with serious garden investment and growing wildlife pressure. Our nearly invisible fence systems are engineered for the salt air and the deer browsing that's reshaped Seacoast gardening.",
    wildlifeNotes:
      "Seacoast deer densities are high year-round, with established coyote populations across Rockingham County. Our deer fences are sized to the local pressure.",
    climateNotes:
      "Coastal New Hampshire weather includes salt spray, full New England winters, and significant snow loads. Hardware is selected for these conditions.",
    lotNotes:
      "From compact village lots in New Castle to multi-acre wooded properties in Stratham and Greenland, we engineer each install to the parcel.",
    whyChoose: [
      { title: "Coastal-corrosion rated", description: "Hardware spec'd for the salt air of the New Hampshire Seacoast." },
      { title: "Real deer protection", description: "Sized for the heavy pressure across Rockingham County." },
      { title: "Cold-climate built", description: "Engineered for full New England winters." },
      { title: "Discreet finish", description: "Preserves the coastal character of Seacoast neighborhoods." },
    ],
    nearbyAreas: ["Rye", "New Castle", "Stratham", "Hampton", "North Hampton", "Greenland", "Hampton Falls", "Rye Beach"],
    localFaqs: [
      ...sharedFaqBase("the New Hampshire Seacoast", "New Hampshire"),
      {
        q: "Will the fence hold up to coastal exposure?",
        a: "Yes. Every component is galvanized and selected for salt-air conditions. We've installed within a few hundred feet of the Atlantic without corrosion concerns.",
      },
    ],
    lat: 43.0009,
    lng: -70.7748,
  },
  {
    slug: "dartmouth-lake-sunapee-nh",
    city: "Dartmouth–Lake Sunapee Region",
    state: "NH",
    region: "Northeast",
    heroImage: galleryDeerLandscape,
    metaTitle: "Fence Installation in Hanover, Lyme & New London, NH | Modern Fence Install",
    metaDescription:
      "Non-electric deer, dog, and garden fence installation across the Dartmouth–Lake Sunapee region — Hanover, Lyme, New London, Etna. Free consultation.",
    intro:
      "The Dartmouth–Lake Sunapee region — Hanover, Lyme, New London, Etna, Norwich (just across the river), Sunapee — combines academic-town wealth with country estates and working New England farms. Deer and bear pressure is significant, and our nearly invisible fence systems protect serious garden investment without disrupting the country views.",
    wildlifeNotes:
      "Upper Valley deer densities are high year-round, with very active bear, coyote, and fisher populations. A real physical fence is the only durable answer for orchards and serious gardens.",
    climateNotes:
      "Upper Valley winters are deep and prolonged. Our hardware and post-setting are spec'd for the snow loads and freeze cycles typical of central New Hampshire.",
    lotNotes:
      "Properties commonly run from 2 to 50+ acres of meadow, woodland, and pasture. We're efficient on long rural perimeters.",
    whyChoose: [
      { title: "Country-property capable", description: "Comfortable on the multi-acre Upper Valley estates and farms." },
      { title: "Deep-winter rated", description: "Built for prolonged New Hampshire freeze cycles." },
      { title: "Real wildlife protection", description: "Sized for the heavy deer and bear pressure of the Upper Valley." },
      { title: "Discreet finish", description: "Preserves the country views that define the region." },
    ],
    nearbyAreas: ["Hanover", "Lyme", "New London", "Etna", "Sunapee", "Enfield", "Plainfield", "Cornish", "Grantham"],
    localFaqs: [
      ...sharedFaqBase("the Dartmouth–Lake Sunapee region", "New Hampshire"),
      {
        q: "Can the fence protect an Upper Valley orchard from bears?",
        a: "Our standard system is sized primarily for deer. For bear-pressured properties we discuss layered perimeter and gate hardware that meaningfully reduce easy access. We'll be straightforward about what a non-electric fence will and won't deter.",
      },
    ],
    lat: 43.7022,
    lng: -72.2896,
  },
  {
    slug: "bucks-county-pa",
    city: "Bucks County",
    state: "PA",
    region: "Northeast",
    heroImage: galleryGermanShepherd,
    metaTitle: "Fence Installation in Bucks County, PA — New Hope, Doylestown, Newtown | Modern Fence Install",
    metaDescription:
      "Non-electric deer, dog, and garden fence installation across Bucks County, PA — New Hope, Doylestown, Newtown, Solebury, Buckingham. Free consultation.",
    intro:
      "Bucks County — New Hope, Doylestown, Newtown, Solebury, Buckingham, Wrightstown — combines historic farmhouses, country estates, and the kind of garden investment that defines the Pennsylvania countryside. Deer pressure is heavy, and our nearly invisible fence systems protect what you've built without disrupting the rural character of the county.",
    wildlifeNotes:
      "Central Bucks County carries some of the densest deer populations in southeastern Pennsylvania, with growing coyote pressure. Our deer fences are sized to the local reality.",
    climateNotes:
      "Pennsylvania weather swings from humid summers to ice storms in winter. Our hardware is selected to handle the full range without rust or sag.",
    lotNotes:
      "Properties commonly run from 2 to 50+ acres of meadow, woodland, and pasture. We're built for the long rural perimeters typical of central Bucks.",
    whyChoose: [
      { title: "Country-property capable", description: "Comfortable on multi-acre Bucks County estates and farms." },
      { title: "Real deer protection", description: "Sized for the heavy pressure across central Bucks." },
      { title: "Discreet from the road", description: "Preserves the historic rural character of the county." },
      { title: "Equestrian-friendly", description: "Layouts that work alongside paddocks and barns." },
    ],
    nearbyAreas: ["New Hope", "Doylestown", "Newtown", "Solebury", "Buckingham", "Wrightstown", "Carversville", "Pipersville", "Pineville", "Furlong"],
    localFaqs: [
      ...sharedFaqBase("Bucks County", "Pennsylvania"),
      {
        q: "Do you install on horse properties in central Bucks?",
        a: "Yes — equestrian properties are a core part of our work in Bucks County. We design layouts that work with paddock fencing, gates, and existing infrastructure.",
      },
    ],
    lat: 40.3401,
    lng: -75.1185,
  },
  {
    slug: "chester-county-pa",
    city: "Chester County",
    state: "PA",
    region: "Northeast",
    heroImage: galleryHound,
    metaTitle: "Fence Installation in Chester County, PA — West Chester, Chadds Ford, Kennett Square | Modern Fence Install",
    metaDescription:
      "Premium non-electric deer, dog, and garden fence installation across Chester County, PA — West Chester, Chadds Ford, Kennett Square, Unionville. Free consultation.",
    intro:
      "Chester County — West Chester, Chadds Ford, Kennett Square, Unionville, Pocopson, East Marlborough — is the heart of Brandywine horse country, with country estates, working farms, and serious garden investment. Our nearly invisible fence systems protect dogs, deer-vulnerable gardens, and equestrian properties without disrupting the open country views that define the region.",
    wildlifeNotes:
      "Chester County deer pressure is among the heaviest in Pennsylvania, with growing coyote populations across the Brandywine Valley. Our deer fences are sized to the local reality.",
    climateNotes:
      "Pennsylvania weather includes humid summers and freeze-thaw winters. Hardware is selected for the full seasonal range.",
    lotNotes:
      "Properties commonly run from 5 to 100+ acres of pasture, woodland, and farmland. We're built for the long rural perimeters typical of horse country.",
    whyChoose: [
      { title: "Horse-country expertise", description: "Comfortable on the multi-acre equestrian properties of the Brandywine Valley." },
      { title: "Real deer protection", description: "Sized for the heavy pressure across Chester County." },
      { title: "Discreet by design", description: "Preserves the open country views of horse-country Pennsylvania." },
      { title: "Long-perimeter capable", description: "Built for the rural property scale of Chester County." },
    ],
    nearbyAreas: ["West Chester", "Chadds Ford", "Kennett Square", "Unionville", "Pocopson", "East Marlborough", "Newtown Square", "Malvern", "Phoenixville", "Coatesville"],
    localFaqs: [
      ...sharedFaqBase("Chester County", "Pennsylvania"),
      {
        q: "Do you install on horse farms in the Brandywine Valley?",
        a: "Yes — Brandywine horse properties are a core part of our work in Chester County. We design layouts that work with paddock fencing, riding rings, and barn complexes.",
      },
    ],
    lat: 39.9610,
    lng: -75.6065,
  },
  {
    slug: "morris-county-nj",
    city: "Morris County",
    state: "NJ",
    region: "Northeast",
    heroImage: galleryHound,
    metaTitle: "Fence Installation in Morris County, NJ — Mendham, Harding, Chester | Modern Fence Install",
    metaDescription:
      "Premium non-electric deer, dog, and garden fence installation across Morris County, NJ — Mendham, Harding, Chester, Bernardsville-adjacent estates. Free consultation.",
    intro:
      "Morris County's western half — Mendham, Harding, Chester, Mendham Township, Long Valley, Washington Township — is some of New Jersey's deepest horse and estate country. Five-acre minimums, mature woodland, and one of the heaviest deer densities in the state make a real fence essential rather than optional. We focus on these rural-edge properties where invisible electric systems simply don't solve the problem.",
    wildlifeNotes:
      "Western Morris County carries punishing deer pressure year-round, with thriving coyote and black-bear populations pushing in from the Highlands. Tick-borne disease pressure is also among the worst in the country — keeping deer physically off the lawn matters for the whole family.",
    climateNotes:
      "North Jersey winters bring deep frost, ice loading, and salt spray on driveways. Posts are set below frost depth and every fastener is hot-dipped galvanized for the full seasonal range.",
    lotNotes:
      "Most installs run 2 to 20+ acres on rolling, wooded lots with stone walls, streams, and irregular boundaries. We engineer the run to follow the land instead of bulldozing through it.",
    whyChoose: [
      { title: "Built for Highlands deer pressure", description: "Heights and mesh sized for one of the heaviest browse zones in the Northeast." },
      { title: "HOA-friendly by design", description: "Low-visual-impact systems blend into mature landscaping and pass the look test in tight ARB neighborhoods." },
      { title: "Equestrian-aware layouts", description: "Comfortable working alongside paddock fencing, riding rings, and barn access." },
      { title: "Frost-rated installs", description: "Posts set deep enough that North Jersey winter heave doesn't move them." },
    ],
    nearbyAreas: ["Mendham", "Harding", "Chester", "Bernardsville", "Long Valley", "Washington Township", "Morristown", "Madison", "Chatham", "New Vernon", "Brookside", "Gladstone"],
    localFaqs: [
      ...sharedFaqBase("Morris County", "New Jersey"),
      {
        q: "Will your fence actually keep North Jersey deer out?",
        a: "Yes. We size deer fences to local pressure — typically 7.5 to 8 feet of nearly invisible mesh in western Morris County. That height is past what local deer will attempt to jump.",
      },
      {
        q: "Do you work on horse properties in Harding and Mendham?",
        a: "Often. We routinely lay out perimeter containment that integrates with existing paddock fencing, riding rings, and barn access without disrupting daily horse operations.",
      },
      {
        q: "Do you install modern fences in NJ?",
        a: "Yes — modern, low-visual-impact fencing is exactly what we do across New Jersey. Instead of bulky traditional perimeter fencing, our engineered steel-rail and cable-mesh system gives you the same containment with a fraction of the visual footprint. We install across Morris, Bergen, Somerset, Hunterdon, Monmouth, and the rest of North/Central NJ.",
      },
      {
        q: "What makes a 'modern' fence different from a traditional NJ fence?",
        a: "Three things: the system mostly disappears from the road, hardware is engineered to outlast cheap big-box fencing (hot-dipped galvanized everything, frost-rated posts), and we engineer the run to follow your terrain instead of bulldozing a straight line through it. That combo is what most NJ HOAs and ARBs are actually looking for.",
      },
    ],
    lat: 40.7968,
    lng: -74.6398,
  },
  {
    slug: "monmouth-county-nj",
    city: "Monmouth County",
    state: "NJ",
    region: "Northeast",
    heroImage: galleryDeerHill,
    metaTitle: "Fence Installation in Monmouth County, NJ — Rumson, Colts Neck, Holmdel | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across Monmouth County, NJ — Rumson, Colts Neck, Holmdel, Middletown, Fair Haven. Free virtual consultation.",
    intro:
      "Monmouth County's coastal-and-horse-country mix — Rumson, Fair Haven, Little Silver, Colts Neck, Holmdel, Middletown, Locust — gives you everything from waterfront village lots to working horse farms. We install on both ends of that spectrum, with hardware specified for salt air on one side of the county and dense deer browse on the other.",
    wildlifeNotes:
      "Inland Monmouth (Colts Neck, Holmdel, Marlboro) deals with serious deer browsing on landscaping and gardens. Coastal areas see less deer but more raccoon and coyote activity that pushes families toward real physical containment over invisible electric systems.",
    climateNotes:
      "Coastal Monmouth gets year-round salt spray that destroys cheap hardware in a few seasons. Every component we install is hot-dipped galvanized and selected to handle salt-laden air alongside humid summers and ice-loaded winters.",
    lotNotes:
      "Coastal village lots run a half-acre to two acres; inland horse-country properties commonly hit 5 to 25 acres. We tailor every install to the parcel rather than running a one-size template.",
    whyChoose: [
      { title: "Salt-air-rated hardware", description: "Galvanized components built to survive Two Rivers and Navesink coastal exposure." },
      { title: "Real deer control inland", description: "Sized correctly for the heavy browse pressure across Colts Neck and Holmdel." },
      { title: "HOA-friendly profile", description: "Discreet enough for Rumson and Fair Haven streetscapes and ARB review." },
      { title: "Clear turnaround", description: "Your confirmed timeline from consultation to completed install is set in writing." },
    ],
    nearbyAreas: ["Rumson", "Fair Haven", "Little Silver", "Colts Neck", "Holmdel", "Middletown", "Locust", "Red Bank", "Lincroft", "Marlboro", "Shrewsbury", "Atlantic Highlands"],
    localFaqs: [
      ...sharedFaqBase("Monmouth County", "New Jersey"),
      {
        q: "Does coastal salt air really damage standard fence hardware?",
        a: "Yes — non-galvanized fasteners and cables in places like Rumson or Sea Bright can show visible corrosion within a few seasons. Everything we install on the coast is hot-dipped galvanized for that reason.",
      },
    ],
    lat: 40.3140,
    lng: -74.1115,
  },
  {
    slug: "howard-county-md",
    city: "Howard County",
    state: "MD",
    region: "Mid-Atlantic",
    heroImage: galleryEstate,
    metaTitle: "Fence Installation in Howard County, MD — Clarksville, Glenelg, Highland | Modern Fence Install",
    metaDescription:
      "Premium non-electric dog, deer, and garden fence installation across Howard County, MD — Clarksville, Glenelg, Highland, Fulton, Cooksville. Free consultation.",
    intro:
      "Western Howard County — Clarksville, Glenelg, Highland, Fulton, Cooksville, West Friendship — is one of the wealthiest large-lot zones between Baltimore and DC. Three-acre minimums in much of the rural-conservation district mean real perimeter runs, real deer pressure on landscaping, and real value in protecting what you've planted.",
    wildlifeNotes:
      "Western Howard sees heavy white-tail browsing and growing coyote pressure pushing in from the Patuxent watershed. Suburban-edge lots in Ellicott City and Columbia also see persistent fox and raccoon activity that drives families toward physical containment.",
    climateNotes:
      "Maryland's clay-heavy soil and freeze-thaw cycles are tough on shallow-set posts. We auger past frost depth and use hardware rated for humid summers and ice-loaded winters alike.",
    lotNotes:
      "Rural-conservation lots typically run 3 to 25+ acres, often with mature woodland edges, streams, and Patuxent-area conservation buffers. We layout to respect those edges instead of fighting them.",
    whyChoose: [
      { title: "Built for clay soils", description: "Posts augered and set deep so Maryland's freeze-thaw cycles don't shift them." },
      { title: "Conservation-buffer aware", description: "Comfortable working around stream buffers and easements common in western Howard." },
      { title: "Real deer protection", description: "Sized for the heavy browse pressure between Clarksville and Glenelg." },
      { title: "HOA-friendly design", description: "Low-visual-impact runs that work in covenant-controlled neighborhoods." },
    ],
    nearbyAreas: ["Clarksville", "Glenelg", "Highland", "Fulton", "Cooksville", "West Friendship", "Dayton", "Woodbine", "Ellicott City", "Columbia", "Sykesville", "Marriottsville"],
    localFaqs: [
      ...sharedFaqBase("Howard County", "Maryland"),
      {
        q: "Can you work around stream buffers and conservation easements in western Howard?",
        a: "Yes — that's a very common situation in Clarksville and Glenelg. We layout the perimeter to stay outside protected buffers and provide spec sheets/renderings if your county or HOA application needs them.",
      },
    ],
    lat: 39.2037,
    lng: -76.8610,
  },
  {
    slug: "richmond-va",
    city: "Greater Richmond",
    state: "VA",
    region: "Mid-Atlantic",
    heroImage: galleryDeerLandscape,
    metaTitle: "Fence Installation in Greater Richmond, VA — West End, Goochland, Manakin-Sabot | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across Greater Richmond — West End, Goochland, Manakin-Sabot, Midlothian, Short Pump. Free consultation.",
    intro:
      "Greater Richmond's western and northern crescent — Goochland, Manakin-Sabot, Short Pump, the West End, Midlothian, Powhatan — combines suburban estates, river-front properties along the James, and serious horse country. Deer pressure runs heavy west of the city, and our nearly invisible fence systems protect landscape and gardens without altering the look of the property.",
    wildlifeNotes:
      "Goochland and western Henrico carry some of the heaviest deer densities in central Virginia. Coyote pressure has grown sharply over the last decade, and James River bottomlands push raccoons and foxes onto inland lots.",
    climateNotes:
      "Central Virginia's humid summers and clay-heavy red soil are the two biggest engineering factors. Posts are set deep into firm soil and every component is corrosion-rated for the full Mid-Atlantic seasonal range.",
    lotNotes:
      "From Short Pump and West End lots in the half-to-two-acre range up to 10–50 acre Goochland farms, we right-size every install to the property.",
    whyChoose: [
      { title: "Engineered for VA clay", description: "Posts set properly in central Virginia's heavy red clay so they don't lean over time." },
      { title: "Real deer protection", description: "Sized for the heavy browse pressure across Goochland and western Henrico." },
      { title: "Horse-country comfortable", description: "Used to working alongside paddocks, riding rings, and barn complexes west of the city." },
      { title: "HOA-friendly profile", description: "Discreet enough to fit West End and Midlothian covenant standards." },
    ],
    nearbyAreas: ["Goochland", "Manakin-Sabot", "Short Pump", "West End", "Midlothian", "Powhatan", "Glen Allen", "Henrico", "Hanover", "Maidens", "Crozier", "Tuckahoe"],
    localFaqs: [
      ...sharedFaqBase("Greater Richmond", "Virginia"),
      {
        q: "Will your fence work on a Goochland horse property?",
        a: "Yes — equestrian properties in Goochland and Powhatan are part of our regular work. We design perimeters that integrate with paddock fencing and barn access without interfering with daily operations.",
      },
    ],
    lat: 37.6543,
    lng: -77.6101,
  },
  {
    slug: "charlottesville-va",
    city: "Charlottesville",
    state: "VA",
    region: "Mid-Atlantic",
    heroImage: galleryEstate,
    metaTitle: "Fence Installation in Charlottesville, VA — Albemarle, Keswick, Ivy | Modern Fence Install",
    metaDescription:
      "Premium non-electric deer, dog, and garden fence installation across Charlottesville and Albemarle County — Keswick, Ivy, Free Union, Earlysville. Free consultation.",
    intro:
      "Charlottesville and surrounding Albemarle County — Keswick, Ivy, Free Union, Earlysville, North Garden, Crozet — is one of the East Coast's most landscape-investment-heavy regions. Vineyards, equestrian estates, and Jefferson-era property layouts deserve a fence that works without changing how the place looks. Deer pressure here is among the worst in Virginia, and our nearly invisible systems are sized to it.",
    wildlifeNotes:
      "Albemarle County deer densities are some of the highest in the state, especially in Keswick, Ivy, and Free Union. Black bear and coyote presence in the western portions of the county is also growing year-over-year.",
    climateNotes:
      "Central Virginia's humid summers, ice-storm winters, and red clay soil drive every spec choice — posts set deep into stable soil with hot-dipped galvanized hardware throughout.",
    lotNotes:
      "Most installs run 5 to 100+ acres of pasture, vineyard, and woodland edge. We're built for long rural perimeters and used to engineering runs that follow rolling Piedmont terrain.",
    whyChoose: [
      { title: "Vineyard & estate experience", description: "Comfortable working around vineyard rows, orchards, and historic outbuildings." },
      { title: "Albemarle deer expertise", description: "Sized for one of the heaviest browse zones in Virginia." },
      { title: "Long-perimeter capable", description: "Built for the multi-acre rural runs typical of Keswick and Free Union." },
      { title: "HOA-friendly profile", description: "Discreet enough to preserve the historic Piedmont character of Albemarle properties." },
    ],
    nearbyAreas: ["Keswick", "Ivy", "Free Union", "Earlysville", "North Garden", "Crozet", "Greenwood", "Batesville", "Scottsville", "Esmont", "Cismont", "Barboursville"],
    localFaqs: [
      ...sharedFaqBase("Charlottesville", "Virginia"),
      {
        q: "Do you install around vineyards and orchards in Albemarle?",
        a: "Yes — vineyard and orchard properties are a core part of our work west of Charlottesville. We design perimeters that respect existing rows, irrigation runs, and equipment access.",
      },
    ],
    lat: 38.0293,
    lng: -78.4767,
  },
  {
    slug: "charleston-sc",
    city: "Greater Charleston",
    state: "SC",
    region: "Southeast",
    heroImage: gardenFenceReal,
    metaTitle: "Fence Installation in Greater Charleston, SC — Mt. Pleasant, Daniel Island, Kiawah | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across Greater Charleston — Mt. Pleasant, Daniel Island, Kiawah, Seabrook, Sullivan's Island. Free consultation.",
    intro:
      "Greater Charleston — Mt. Pleasant, Daniel Island, Kiawah, Seabrook, Sullivan's Island, Isle of Palms, and the historic peninsula's western suburbs — combines coastal lifestyle, gated-community standards, and serious wildlife pressure from deer to alligators to raccoons. Our nearly invisible fence systems are spec'd for Lowcountry salt air and built to disappear into the landscaping that defines the region.",
    wildlifeNotes:
      "Lowcountry deer pressure on landscaping is heavier than most newcomers expect, especially on Kiawah, Seabrook, and the I'On / Daniel Island corridors. Raccoons, foxes, and the occasional coyote round out the case for real physical containment over invisible electric systems.",
    climateNotes:
      "Charleston's combination of subtropical humidity, salt-laden coastal air, and hurricane-season wind loads is brutal on cheap fencing. Every fastener and cable we install is hot-dipped galvanized and engineered for the Lowcountry environment.",
    lotNotes:
      "Coastal-island and gated-community lots commonly run a half-acre to three acres; inland Mt. Pleasant and Awendaw properties stretch larger. We tailor each install to the parcel and the salt-exposure level.",
    whyChoose: [
      { title: "Lowcountry salt-air spec", description: "Hot-dipped galvanized hardware built to survive Kiawah and Seabrook coastal exposure." },
      { title: "HOA-friendly design", description: "Discreet enough for Daniel Island, I'On, and Kiawah architectural review standards." },
      { title: "Real deer & raccoon control", description: "Physical fence that works 24/7 — no collars, no batteries, no training." },
      { title: "Hurricane-aware engineering", description: "Cables tensioned and hardware specified to handle Lowcountry storm-season loads." },
    ],
    nearbyAreas: ["Mt. Pleasant", "Daniel Island", "Kiawah Island", "Seabrook Island", "Sullivan's Island", "Isle of Palms", "James Island", "Johns Island", "Awendaw", "I'On", "West Ashley", "Wadmalaw Island"],
    localFaqs: [
      ...sharedFaqBase("Greater Charleston", "South Carolina"),
      {
        q: "Will the fence pass Kiawah or Daniel Island ARB review?",
        a: "In our experience, yes. The nearly invisible profile of our system is a strong fit for the design standards on Kiawah, Seabrook, and Daniel Island. We can provide spec sheets and renderings to support your application.",
      },
      {
        q: "Does coastal salt air really damage fence hardware that fast?",
        a: "Yes — non-galvanized hardware on the barrier islands can show visible corrosion within a couple of seasons. Everything we install in the Lowcountry is hot-dipped galvanized for that reason.",
      },
    ],
    lat: 32.7765,
    lng: -79.9311,
  },
  {
    slug: "north-shore-ma",
    city: "North Shore Massachusetts",
    state: "MA",
    region: "Northeast",
    heroImage: galleryEstate,
    metaTitle: "Fence Installation on the North Shore MA — Manchester, Hamilton, Wenham | Modern Fence Install",
    metaDescription:
      "Premium non-electric dog, deer, and garden fence installation across Boston's North Shore — Manchester-by-the-Sea, Hamilton, Wenham, Beverly Farms, Topsfield. Free consultation.",
    intro:
      "Boston's North Shore — Manchester-by-the-Sea, Hamilton, Wenham, Beverly Farms, Topsfield, Boxford, Ipswich — is one of New England's quietest pockets of true estate country. Polo grounds in Hamilton, working orchards in Topsfield, salt-marsh frontage in Manchester and Essex. Deer pressure here is heavy, salt air is a real factor near the coast, and the architectural standards are unforgiving. Our nearly invisible fences fit all three.",
    wildlifeNotes:
      "North Shore deer densities have climbed sharply over the last decade, especially in Hamilton, Wenham, Topsfield, and Boxford. Coyote sightings are routine, and tick-borne disease pressure is among the worst in the state — keeping deer physically off the lawn matters for the whole family.",
    climateNotes:
      "Coastal North Shore properties get year-round salt spray that destroys cheap hardware. Inland properties face deep frost and heavy snow loads. Every fastener and cable we install is hot-dipped galvanized and rated for both environments.",
    lotNotes:
      "Coastal village lots in Manchester and Beverly Farms run a half-acre to two acres; Hamilton, Topsfield, and Boxford properties commonly hit 5 to 25+ acres of pasture and woodland. We tailor every install to the parcel and the salt-exposure level.",
    whyChoose: [
      { title: "Salt-air-rated hardware", description: "Galvanized components built to survive Manchester and Beverly Farms coastal exposure." },
      { title: "Estate & equestrian comfortable", description: "Used to working alongside paddocks, riding rings, and barn complexes in Hamilton and Wenham." },
      { title: "HOA-friendly profile", description: "Discreet enough for the strictest North Shore architectural standards." },
      { title: "Frost-rated installs", description: "Posts set deep enough that New England winter heave doesn't move them." },
    ],
    nearbyAreas: ["Manchester-by-the-Sea", "Hamilton", "Wenham", "Beverly Farms", "Topsfield", "Boxford", "Ipswich", "Essex", "Prides Crossing", "Magnolia", "South Hamilton", "Rowley"],
    localFaqs: [
      ...sharedFaqBase("North Shore Massachusetts", "Massachusetts"),
      {
        q: "Do you install on horse properties in Hamilton and Wenham?",
        a: "Often. Hamilton-Wenham is one of New England's strongest equestrian zones and we routinely lay out perimeters that integrate with paddock fencing, riding rings, and barn access without disrupting daily horse operations.",
      },
      {
        q: "Will salt air really damage standard fence hardware in Manchester or Beverly Farms?",
        a: "Yes — non-galvanized fasteners and cables in coastal North Shore towns can show visible corrosion within a couple of seasons. Everything we install on the coast is hot-dipped galvanized for that reason.",
      },
    ],
    lat: 42.5778,
    lng: -70.7717,
  },
  {
    slug: "south-shore-ma",
    city: "South Shore Massachusetts",
    state: "MA",
    region: "Northeast",
    heroImage: hiddenFence,
    metaTitle: "Fence Installation on the South Shore MA — Cohasset, Duxbury, Hingham | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across Boston's South Shore — Cohasset, Duxbury, Hingham, Marshfield, Norwell, Scituate. Free consultation.",
    intro:
      "Boston's South Shore — Cohasset, Duxbury, Hingham, Marshfield, Norwell, Scituate, Hanover — combines waterfront village lots, wooded inland estates, and some of the strongest school-town demographics in New England. Deer browse on hostas and hydrangeas is a chronic complaint, and our nearly invisible fence systems solve it without changing the look of the property.",
    wildlifeNotes:
      "South Shore deer pressure on landscaping is heavy across Norwell, Hanover, and inland Duxbury. Coyote populations are well-established and rising, and coastal raccoon and fox activity drives families toward physical containment over invisible electric collars.",
    climateNotes:
      "Coastal South Shore properties take steady salt spray and nor'easter wind loads; inland sites get deep frost and heavy snow. Hardware is hot-dipped galvanized throughout and posts are set below frost depth.",
    lotNotes:
      "Cohasset, Hingham, and Scituate village lots typically run a half-acre to two acres; Norwell, Hanover, and Duxbury properties stretch to 3–10+ acres with mature woodland edges.",
    whyChoose: [
      { title: "Coastal-spec hardware", description: "Galvanized components built for Cohasset and Scituate salt exposure." },
      { title: "Real deer protection inland", description: "Sized for the heavy browse pressure across Norwell and Duxbury." },
      { title: "HOA-friendly design", description: "Low-visual-impact runs that fit Cohasset and Hingham streetscapes." },
      { title: "Clear turnaround", description: "Your confirmed timeline from consultation to completed install is set in writing." },
    ],
    nearbyAreas: ["Cohasset", "Duxbury", "Hingham", "Marshfield", "Norwell", "Scituate", "Hanover", "Pembroke", "Kingston", "Plymouth", "Milton", "Westwood"],
    localFaqs: [
      ...sharedFaqBase("South Shore Massachusetts", "Massachusetts"),
      {
        q: "Will deer fence work on a Duxbury or Norwell wooded lot?",
        a: "Yes — wooded inland lots are where our deer fence shines. We layout the perimeter to follow the trees rather than clearing them, and the system disappears against the woodland backdrop.",
      },
    ],
    lat: 42.1359,
    lng: -70.7064,
  },
  {
    slug: "anne-arundel-county-md",
    city: "Anne Arundel County",
    state: "MD",
    region: "Mid-Atlantic",
    heroImage: deerFenceReal,
    metaTitle: "Fence Installation in Anne Arundel County, MD — Annapolis, Severna Park, Davidsonville | Modern Fence Install",
    metaDescription:
      "Premium non-electric dog, deer, and garden fence installation across Anne Arundel County — Annapolis, Severna Park, Davidsonville, Crownsville, Edgewater. Free consultation.",
    intro:
      "Anne Arundel County combines Chesapeake waterfront in Annapolis, Severna Park, and Edgewater with serious horse-and-estate country in Davidsonville, Crownsville, and West River. Deer pressure is heavy on inland landscaping, salt and brackish-water exposure is real along the Bay, and our nearly invisible fence systems are spec'd for both environments.",
    wildlifeNotes:
      "Inland Anne Arundel — Davidsonville, Crownsville, Harwood — sees heavy deer browsing and growing coyote pressure. Waterfront properties closer to the Bay add raccoons, foxes, and the occasional river otter to the mix that drives families toward real physical containment.",
    climateNotes:
      "Chesapeake humidity, brackish-water spray, and clay-heavy soil drive every spec choice. Posts are augered into stable subsoil and every fastener is hot-dipped galvanized for the full Mid-Atlantic seasonal range.",
    lotNotes:
      "Waterfront Annapolis and Severna Park lots commonly run a half-acre to three acres; Davidsonville and West River properties stretch to 5–25+ acres of pasture and woodland.",
    whyChoose: [
      { title: "Built for Chesapeake exposure", description: "Hardware specified for the brackish-air environment along the Severn and South Rivers." },
      { title: "Equestrian-aware in Davidsonville", description: "Comfortable working alongside paddocks, riding rings, and barn access." },
      { title: "Real deer protection", description: "Sized for the heavy browse pressure across western Anne Arundel." },
      { title: "HOA-friendly profile", description: "Discreet enough for Severna Park and Annapolis covenant standards." },
    ],
    nearbyAreas: ["Annapolis", "Severna Park", "Davidsonville", "Crownsville", "Edgewater", "Arnold", "Cape St. Claire", "West River", "Harwood", "Gibson Island", "Millersville", "Pasadena"],
    localFaqs: [
      ...sharedFaqBase("Anne Arundel County", "Maryland"),
      {
        q: "Do you work on waterfront properties along the Severn or South River?",
        a: "Yes — waterfront installs are part of our regular work in Anne Arundel. We use hot-dipped galvanized hardware throughout to handle the brackish-air environment and design layouts that respect critical-area buffers.",
      },
    ],
    lat: 38.9784,
    lng: -76.4922,
  },
  {
    slug: "baltimore-county-horse-country-md",
    city: "Baltimore County Horse Country",
    state: "MD",
    region: "Mid-Atlantic",
    heroImage: galleryEstate,
    metaTitle: "Fence Installation in Baltimore County Horse Country, MD — Monkton, Glyndon, Worthington Valley | Modern Fence Install",
    metaDescription:
      "Non-electric deer, dog, and garden fence installation across Baltimore County's horse country — Monkton, Glyndon, Owings Mills, Greenspring Valley, Worthington Valley. Free consultation.",
    intro:
      "Baltimore County's horse country — Monkton, Glyndon, Owings Mills, Greenspring Valley, Worthington Valley, Butler, Phoenix, Sparks — is one of the East Coast's deepest pockets of working equestrian estates. Hunt clubs, polo grounds, and multi-generational farms fill the rolling Piedmont north and west of the city. Deer pressure is heavy, garden investment is serious, and our nearly invisible fence systems protect both without changing the look of the land.",
    wildlifeNotes:
      "Northern Baltimore County deer densities are among the heaviest in Maryland. Coyote populations have grown sharply across the Worthington and Greenspring Valleys, and tick-borne disease pressure is a year-round concern.",
    climateNotes:
      "Maryland Piedmont weather brings humid summers, ice-storm winters, and clay-heavy red soil. Posts are set deep into firm subsoil and every component is corrosion-rated for the full seasonal range.",
    lotNotes:
      "Most installs run 5 to 100+ acres of pasture, woodland, and training-track frontage. We're built for long rural perimeters and used to engineering runs that follow Piedmont contours.",
    whyChoose: [
      { title: "Horse-country expertise", description: "Comfortable on the multi-acre equestrian properties of the Worthington and Greenspring Valleys." },
      { title: "Long-perimeter capable", description: "Built for the rural property scale typical of Monkton, Butler, and Sparks." },
      { title: "Real deer protection", description: "Sized for one of the heaviest browse zones in Maryland." },
      { title: "HOA-friendly profile", description: "Discreet enough to preserve the historic open-country character of Baltimore County's hunt country." },
    ],
    nearbyAreas: ["Monkton", "Glyndon", "Owings Mills", "Reisterstown", "Butler", "Phoenix", "Sparks", "Stevenson", "Pikesville", "Lutherville", "Cockeysville", "Parkton"],
    localFaqs: [
      ...sharedFaqBase("Baltimore County Horse Country", "Maryland"),
      {
        q: "Do you install on working horse farms in Monkton or Butler?",
        a: "Yes — working equestrian properties are a core part of our work in northern Baltimore County. We design perimeters that integrate with paddock fencing, training tracks, and barn access without interfering with daily horse operations.",
      },
    ],
    lat: 39.5790,
    lng: -76.6260,
  },
  {
    slug: "lancaster-county-pa",
    city: "Lancaster County",
    state: "PA",
    region: "Northeast",
    heroImage: galleryDeerHill,
    metaTitle: "Fence Installation in Lancaster County, PA — Lititz, Strasburg, Manheim Township | Modern Fence Install",
    metaDescription:
      "Premium non-electric deer, dog, and garden fence installation across Lancaster County, PA — Lititz, Strasburg, Manheim Township, Lampeter, Willow Street. Free consultation.",
    intro:
      "Lancaster County — Lititz, Strasburg, Manheim Township, Lampeter, Willow Street, Bird-in-Hand — combines some of the most productive farmland in the country with a growing belt of large-lot suburban estates north and east of Lancaster City. Deer pressure on gardens and ornamental landscaping is heavy year-round, and our nearly invisible fence systems protect what you've planted without disrupting the open Pennsylvania Dutch country views.",
    wildlifeNotes:
      "Lancaster County deer densities are among the highest in Pennsylvania, especially around the Welsh Mountain edge and the Susquehanna riparian corridor. Groundhog and raccoon pressure on vegetable gardens is also a year-round factor.",
    climateNotes:
      "Pennsylvania Piedmont weather includes humid summers, freeze-thaw winters, and limestone-influenced soil. Hardware is hot-dipped galvanized throughout and posts are set deep enough to handle the freeze-thaw cycle.",
    lotNotes:
      "Suburban Lititz and Manheim Township lots typically run a half-acre to three acres; rural properties around Strasburg, Bird-in-Hand, and the Welsh Mountain stretch to 5–50+ acres.",
    whyChoose: [
      { title: "Garden-protection focus", description: "Engineered for the heavy deer and groundhog pressure that defines Lancaster County gardens." },
      { title: "Long-perimeter capable", description: "Built for the multi-acre rural property scale typical of southern Lancaster County." },
      { title: "HOA-friendly profile", description: "Low-visual-impact runs that fit Lititz and Manheim Township covenant neighborhoods." },
      { title: "Discreet by design", description: "Preserves the open Pennsylvania Dutch country views that define the region." },
    ],
    nearbyAreas: ["Lititz", "Strasburg", "Manheim Township", "Lampeter", "Willow Street", "Bird-in-Hand", "Leola", "Ephrata", "Akron", "Mount Joy", "Elizabethtown", "Gap"],
    localFaqs: [
      ...sharedFaqBase("Lancaster County", "Pennsylvania"),
      {
        q: "Will the fence really stop deer from destroying my Lancaster County garden?",
        a: "Yes — that's exactly what our garden and deer fence systems are sized for. We use 7.5 to 8 feet of nearly invisible mesh in heavy-pressure areas, which is past what local deer will attempt to jump.",
      },
    ],
    lat: 40.0379,
    lng: -76.3055,
  },
  {
    slug: "savannah-ga",
    city: "Greater Savannah",
    state: "GA",
    region: "Southeast",
    heroImage: galleryDeerLandscape,
    metaTitle: "Fence Installation in Greater Savannah, GA — Skidaway, The Landings, Ford Plantation | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across Greater Savannah — Skidaway Island, The Landings, Ford Plantation, Ardsley Park, Isle of Hope. Free consultation.",
    intro:
      "Greater Savannah — Skidaway Island, The Landings, Ford Plantation, Ardsley Park, Isle of Hope, Wilmington Island, Richmond Hill — combines historic Lowcountry character with some of the South's most exclusive gated communities. Deer pressure on landscaping is heavier than newcomers expect, salt-air exposure is constant, and our nearly invisible fence systems are spec'd to disappear into the moss-draped landscape that defines the region.",
    wildlifeNotes:
      "Lowcountry deer browsing on ornamental landscaping is a chronic complaint on Skidaway Island, The Landings, and Ford Plantation. Raccoon, fox, and the occasional alligator round out the case for real physical containment over invisible electric collars.",
    climateNotes:
      "Coastal Georgia's subtropical humidity, year-round salt-laden air, and hurricane-season wind loads are brutal on cheap fence hardware. Every fastener and cable we install is hot-dipped galvanized and engineered for the Lowcountry environment.",
    lotNotes:
      "Gated-community lots on Skidaway and The Landings commonly run a half-acre to two acres; Ford Plantation and inland Richmond Hill properties stretch to 3–10+ acres with mature live-oak canopy.",
    whyChoose: [
      { title: "Lowcountry salt-air spec", description: "Hot-dipped galvanized hardware built to survive Skidaway and Wilmington Island coastal exposure." },
      { title: "HOA-friendly design", description: "Discreet enough for The Landings, Ford Plantation, and Skidaway architectural review standards." },
      { title: "Real deer & raccoon control", description: "Physical fence that works 24/7 — no collars, no batteries, no training." },
      { title: "Hurricane-aware engineering", description: "Cables tensioned and hardware specified to handle Lowcountry storm-season loads." },
    ],
    nearbyAreas: ["Skidaway Island", "The Landings", "Ford Plantation", "Ardsley Park", "Isle of Hope", "Wilmington Island", "Richmond Hill", "Tybee Island", "Whitemarsh Island", "Pooler", "Thunderbolt", "Ardmore"],
    localFaqs: [
      ...sharedFaqBase("Greater Savannah", "Georgia"),
      {
        q: "Will the fence pass Skidaway Island or The Landings ARB review?",
        a: "In our experience, yes. The nearly invisible profile of our system is a strong fit for the design standards on Skidaway, The Landings, and Ford Plantation. We can provide spec sheets and renderings to support your application.",
      },
      {
        q: "Does coastal salt air really damage fence hardware that fast in coastal Georgia?",
        a: "Yes — non-galvanized hardware on the barrier islands and tidal-creek frontage can show visible corrosion within a couple of seasons. Everything we install in the Lowcountry is hot-dipped galvanized for that reason.",
      },
    ],
    lat: 32.0809,
    lng: -81.0912,
  },
  {
    slug: "cape-ann-ma",
    city: "Cape Ann",
    state: "MA",
    region: "Northeast",
    heroImage: galleryHound,
    metaTitle: "Fence Installation on Cape Ann, MA — Gloucester, Rockport, Essex | Modern Fence Install",
    metaDescription:
      "Premium non-electric dog, deer, and garden fence installation across Cape Ann — Gloucester, Rockport, Essex, Annisquam, Lanesville. Coastal-rated hardware. Free consultation.",
    intro:
      "Cape Ann — Gloucester, Rockport, Essex, Annisquam, Lanesville, Magnolia — sits at the northern edge of Massachusetts Bay where granite ledge, salt marsh, and Atlantic spray define every property. Architecture-conscious villages, working harbors, and quiet inland woodlots all share the same problem: the wrong fence hardware fails inside two seasons here. Our nearly invisible systems are spec'd specifically for the Cape Ann salt environment.",
    wildlifeNotes:
      "Cape Ann deer pressure has climbed steadily as inland Essex County woodlots fill in. Coyote sightings are routine in Rockport and Lanesville, and the salt-marsh edges in Essex and Annisquam add raccoon and fox traffic that drives families toward physical containment.",
    climateNotes:
      "Constant Atlantic salt spray, hard granite ledge underfoot, and nor'easter wind loads define every Cape Ann install. We use hot-dipped galvanized hardware throughout and adapt anchoring methods where ledge prevents deep auger sets.",
    lotNotes:
      "Village lots in Rockport, Annisquam, and East Gloucester typically run a quarter-acre to a full acre; inland Essex and West Gloucester properties stretch to 2–10+ acres of mixed woodland and meadow.",
    whyChoose: [
      { title: "True coastal hardware spec", description: "Hot-dipped galvanized fasteners and cables built to survive year-round Atlantic salt spray." },
      { title: "Ledge-aware install methods", description: "Adapted anchoring techniques where Cape Ann granite prevents standard deep-auger post sets." },
      { title: "HOA-friendly profile", description: "Discreet enough for Rockport and Annisquam village architectural standards." },
      { title: "Real coyote & deer control", description: "Physical fence that works through fog, snow, and storm season — no batteries, no collars." },
    ],
    nearbyAreas: ["Gloucester", "Rockport", "Essex", "Annisquam", "Lanesville", "Magnolia", "East Gloucester", "West Gloucester", "Pigeon Cove", "Bay View", "Manchester-by-the-Sea", "Ipswich"],
    localFaqs: [
      ...sharedFaqBase("Cape Ann", "Massachusetts"),
      {
        q: "Can you install on a Rockport or Annisquam property with granite ledge close to the surface?",
        a: "Yes — ledge is the rule rather than the exception on Cape Ann. We adapt our anchoring approach where deep auger sets aren't possible and still deliver a fully tensioned, long-lived perimeter.",
      },
      {
        q: "Will salt spray really destroy a fence in Gloucester or Lanesville that fast?",
        a: "Yes — non-galvanized hardware on the immediate coast can show visible corrosion within a season or two. Everything we install on Cape Ann is hot-dipped galvanized for that reason.",
      },
    ],
    lat: 42.6159,
    lng: -70.6620,
  },
  {
    slug: "north-fork-long-island-ny",
    city: "North Fork Long Island",
    state: "NY",
    region: "Northeast",
    heroImage: galleryDeerLandscape,
    metaTitle: "Fence Installation on the North Fork, NY — Southold, Cutchogue, Mattituck | Modern Fence Install",
    metaDescription:
      "Non-electric deer, dog, and garden fence installation across Long Island's North Fork — Southold, Cutchogue, Mattituck, Greenport, Orient. Vineyard-aware. Free consultation.",
    intro:
      "Long Island's North Fork — Southold, Cutchogue, Mattituck, Greenport, Orient, Jamesport, New Suffolk — is wine country, farm country, and waterfront country layered into one peninsula. Vineyards, sod farms, and historic farmhouses share a coastline with serious second-home estates. Deer pressure on ornamental landscaping and young vines is heavy, and our nearly invisible fence systems protect what you've planted without breaking the open North Fork sightlines.",
    wildlifeNotes:
      "North Fork deer densities are notoriously heavy — vineyard managers from Mattituck to Orient routinely cite browse damage as a top operating cost. Rabbit and raccoon pressure on home gardens is also constant year-round.",
    climateNotes:
      "Peconic Bay and Long Island Sound salt air on both shores, sandy loam soil, and steady wind exposure shape every install. Hardware is hot-dipped galvanized throughout and posts are sized for the loose-soil conditions.",
    lotNotes:
      "Hamlet lots in Southold, Cutchogue, and Greenport commonly run a half-acre to two acres; agricultural and estate parcels in Mattituck, Orient, and Jamesport stretch to 5–50+ acres.",
    whyChoose: [
      { title: "Vineyard- and orchard-aware", description: "Used to working alongside working vineyards and sod farms across Cutchogue and Mattituck." },
      { title: "Salt-air-rated hardware", description: "Galvanized components built for both Peconic Bay and Long Island Sound exposure." },
      { title: "Real deer protection", description: "Sized for one of the heaviest browse zones on Long Island." },
      { title: "Discreet by design", description: "Preserves the open North Fork farm and water sightlines that define the region." },
    ],
    nearbyAreas: ["Southold", "Cutchogue", "Mattituck", "Greenport", "Orient", "Jamesport", "New Suffolk", "Peconic", "Laurel", "East Marion", "Aquebogue", "Riverhead"],
    localFaqs: [
      ...sharedFaqBase("North Fork Long Island", "New York"),
      {
        q: "Do you install on working vineyards or farms on the North Fork?",
        a: "Yes — vineyard and farm-edge perimeters are part of our regular work in Cutchogue, Mattituck, and Jamesport. We layout the fence to respect rows, equipment access, and irrigation without disrupting operations.",
      },
      {
        q: "How tall does the fence need to be to actually stop North Fork deer?",
        a: "We typically spec 7.5 to 8 feet of nearly invisible mesh on the North Fork — that's past what local deer will attempt to jump even under heavy browse pressure.",
      },
    ],
    lat: 41.0640,
    lng: -72.4262,
  },
  {
    slug: "sussex-county-nj",
    city: "Sussex County",
    state: "NJ",
    region: "Northeast",
    heroImage: galleryDeerHill,
    metaTitle: "Fence Installation in Sussex County, NJ — Sparta, Vernon, Andover | Modern Fence Install",
    metaDescription:
      "Premium non-electric deer, dog, and garden fence installation across Sussex County, NJ — Sparta, Vernon, Andover, Hardyston, Wantage. Lake and horse country. Free consultation.",
    intro:
      "Sussex County is northwest New Jersey at its most rural — Sparta, Vernon, Andover, Hardyston, Wantage, Stillwater, Frankford. Lake communities like Lake Mohawk and Lake Hopatcong sit alongside working horse farms, orchards, and the largest unbroken stretch of black-bear and deer country in the state. Our nearly invisible fence systems are sized for that wildlife pressure without changing the look of the land.",
    wildlifeNotes:
      "Sussex County has the densest deer and black-bear populations in New Jersey. Coyote and bobcat sightings are now routine across Vernon, Hardyston, and Wantage, and tick-borne disease pressure is severe — physical containment matters here.",
    climateNotes:
      "Northwest NJ Highlands weather brings deep frost, heavy snow loads, and rocky glacial-till soil. Posts are set deep into firm subsoil and every fastener is hot-dipped galvanized for the full seasonal range.",
    lotNotes:
      "Lake-community lots around Sparta and Lake Mohawk typically run a half-acre to two acres; rural and equestrian parcels in Andover, Wantage, and Stillwater stretch to 5–50+ acres.",
    whyChoose: [
      { title: "Built for heavy wildlife", description: "Sized for the deer, bear, and coyote pressure that defines northwest New Jersey." },
      { title: "Equestrian-aware", description: "Comfortable working alongside paddocks, riding rings, and barn complexes in Andover and Hardyston." },
      { title: "Frost- and rock-rated installs", description: "Adapted methods for Highlands glacial-till soil and deep New Jersey frost." },
      { title: "HOA-friendly profile", description: "Discreet enough for Lake Mohawk and Sparta covenant communities." },
    ],
    nearbyAreas: ["Sparta", "Vernon", "Andover", "Hardyston", "Wantage", "Stillwater", "Frankford", "Hampton", "Byram", "Newton", "Lake Mohawk", "Lake Hopatcong"],
    localFaqs: [
      ...sharedFaqBase("Sussex County", "New Jersey"),
      {
        q: "Will the fence hold up against black bears in Vernon or Hardyston?",
        a: "Our system is engineered for deer and dog containment, not as an active bear barrier — but the tensioned mesh and post spacing we use in Sussex County hold up far better than typical residential fencing under incidental bear contact.",
      },
      {
        q: "Do you install on lake-community lots around Lake Mohawk or Lake Hopatcong?",
        a: "Often. We're familiar with the architectural review processes in those associations and our low-visual-impact profile is generally well received.",
      },
    ],
    lat: 41.1390,
    lng: -74.6915,
  },
  {
    slug: "talbot-county-md",
    city: "Talbot County",
    state: "MD",
    region: "Mid-Atlantic",
    heroImage: galleryEstate,
    metaTitle: "Fence Installation in Talbot County, MD — Easton, St. Michaels, Oxford | Modern Fence Install",
    metaDescription:
      "Non-electric dog, deer, and garden fence installation across Maryland's Eastern Shore — Easton, St. Michaels, Oxford, Tilghman Island, Royal Oak. Waterfront-rated. Free consultation.",
    intro:
      "Talbot County — Easton, St. Michaels, Oxford, Tilghman Island, Royal Oak, Trappe — is the heart of Maryland's Eastern Shore. Working watermen's villages, historic waterfront estates on the Miles, Tred Avon, and Choptank Rivers, and inland farm parcels all share the same Chesapeake conditions. Our nearly invisible fence systems are spec'd for brackish-air exposure, deer pressure, and the architectural sensitivity of historic Eastern Shore villages.",
    wildlifeNotes:
      "Eastern Shore deer densities are heavy across inland Talbot, and Sika deer pressure rises as you move south toward Dorchester. Waterfront properties add raccoons, foxes, and the occasional river otter to the case for real physical containment.",
    climateNotes:
      "Chesapeake humidity, brackish-water spray on the Miles and Tred Avon, and clay-loam soil drive every spec choice. Hardware is hot-dipped galvanized throughout and posts are augered into stable subsoil.",
    lotNotes:
      "Historic-village lots in Easton, St. Michaels, and Oxford typically run a quarter-acre to two acres; waterfront estates and inland farm parcels stretch to 5–50+ acres along the Miles, Tred Avon, and Choptank.",
    whyChoose: [
      { title: "Eastern Shore waterfront spec", description: "Hardware specified for the brackish-air environment along the Miles, Tred Avon, and Choptank Rivers." },
      { title: "Historic-village discreet", description: "Low-visual-impact runs that fit Easton, St. Michaels, and Oxford historic district sensibilities." },
      { title: "Real deer protection", description: "Sized for the heavy browse pressure across inland Talbot County." },
      { title: "Critical-area aware", description: "Layouts that respect Chesapeake critical-area buffers and existing waterfront vegetation." },
    ],
    nearbyAreas: ["Easton", "St. Michaels", "Oxford", "Tilghman Island", "Royal Oak", "Trappe", "Cordova", "Wittman", "Bozman", "Newcomb", "Sherwood", "Queen Anne"],
    localFaqs: [
      ...sharedFaqBase("Talbot County", "Maryland"),
      {
        q: "Can you install on a waterfront property in St. Michaels or Oxford with critical-area restrictions?",
        a: "Yes — Eastern Shore waterfront installs are part of our regular Talbot County work. We design layouts that respect Chesapeake critical-area buffers and use hot-dipped galvanized hardware throughout to handle the brackish-air environment.",
      },
      {
        q: "Will the design clear the historic district standards in Easton or St. Michaels?",
        a: "In our experience, our nearly invisible profile is a strong fit for historic district reviews. We can provide spec sheets and renderings to support your application — securing approval is the homeowner's responsibility.",
      },
    ],
    lat: 38.7745,
    lng: -76.0763,
  },
  {
    slug: "williamsburg-va",
    city: "Greater Williamsburg",
    state: "VA",
    region: "Mid-Atlantic",
    heroImage: galleryHound,
    metaTitle: "Fence Installation in Greater Williamsburg, VA — Williamsburg, Kingsmill, Toano | Modern Fence Install",
    metaDescription:
      "Premium non-electric dog, deer, and garden fence installation across Greater Williamsburg — Williamsburg, Kingsmill, Toano, Lightfoot, New Kent. Free consultation.",
    intro:
      "Greater Williamsburg — Williamsburg proper, Kingsmill, Ford's Colony, Governor's Land, Toano, Lightfoot, Norge, New Kent — combines historic-district sensibility with some of southeastern Virginia's strongest gated and equestrian communities. Deer pressure on landscaping is heavy across the wooded peninsula, and our nearly invisible fence systems disappear into the historic Tidewater backdrop.",
    wildlifeNotes:
      "Williamsburg-area deer pressure is heavy and constant — the wooded peninsula geography keeps populations densely concentrated. Coyote sightings are now routine across New Kent and James City County, and raccoon pressure on gardens is a year-round concern.",
    climateNotes:
      "Tidewater Virginia humidity, mild but freeze-prone winters, and clay-rich soil shape every install. Posts are set into stable subsoil and every fastener is hot-dipped galvanized for the full seasonal range.",
    lotNotes:
      "Gated-community lots in Kingsmill, Ford's Colony, and Governor's Land typically run a half-acre to two acres; rural James City and New Kent properties stretch to 5–25+ acres of wooded acreage.",
    whyChoose: [
      { title: "Gated-community discreet", description: "Low-visual-impact runs that fit Kingsmill, Ford's Colony, and Governor's Land architectural review standards." },
      { title: "Real deer protection", description: "Sized for the heavy browse pressure across the wooded Williamsburg peninsula." },
      { title: "Historic-district sensitive", description: "Designs that respect the colonial Tidewater character of the broader Williamsburg area." },
      { title: "HOA-friendly profile", description: "Discreet enough for the strictest Greater Williamsburg covenant communities." },
    ],
    nearbyAreas: ["Williamsburg", "Kingsmill", "Ford's Colony", "Governor's Land", "Toano", "Lightfoot", "Norge", "New Kent", "James City", "Yorktown", "Poquoson", "Providence Forge"],
    localFaqs: [
      ...sharedFaqBase("Greater Williamsburg", "Virginia"),
      {
        q: "Will the fence pass Kingsmill or Ford's Colony architectural review?",
        a: "In our experience, yes. The nearly invisible profile of our system is a strong fit for the design standards in Greater Williamsburg's gated communities. We can provide spec sheets and renderings to support your application — securing approval is the homeowner's responsibility.",
      },
    ],
    lat: 37.2707,
    lng: -76.7075,
  },
  {
    slug: "aiken-sc",
    city: "Aiken",
    state: "SC",
    region: "Southeast",
    heroImage: galleryEstate,
    metaTitle: "Fence Installation in Aiken, SC — Aiken, Wagener, Montmorenci | Modern Fence Install",
    metaDescription:
      "Non-electric deer, dog, and garden fence installation across Aiken, SC — Aiken proper, Wagener, Montmorenci, Windsor, Beech Island. Equestrian-aware. Free consultation.",
    intro:
      "Aiken is the equestrian capital of the Southeast — winter training grounds for thoroughbred, polo, eventing, and steeplechase operations from across the country. Hitchcock Woods, the Aiken Training Track, and miles of unpaved horse trails define a culture built around large-acre estates, working barns, and serious garden investment. Our nearly invisible fence systems integrate with that horse-country fabric without disrupting it.",
    wildlifeNotes:
      "Aiken-area deer pressure is heavy and consistent across the sandhills. Coyote populations have grown sharply in Aiken and Edgefield Counties, and feral hog pressure is a real factor on rural and equestrian parcels south and east of town.",
    climateNotes:
      "Sandhills South Carolina weather brings hot humid summers, mild winters, and deep sandy loam soil. Posts are sized for sandy-soil anchoring and hardware is hot-dipped galvanized for the full seasonal range.",
    lotNotes:
      "In-town historic Aiken lots typically run a half-acre to two acres; equestrian and rural parcels in Wagener, Montmorenci, Windsor, and Beech Island stretch to 5–100+ acres of pasture, woodland, and training infrastructure.",
    whyChoose: [
      { title: "Horse-country expertise", description: "Comfortable on the multi-acre training operations that define Aiken's equestrian culture." },
      { title: "Long-perimeter capable", description: "Built for the rural-estate scale typical of Wagener, Montmorenci, and Windsor." },
      { title: "Sandy-soil install methods", description: "Adapted post-setting techniques for the deep sandhills soil profile." },
      { title: "Discreet by design", description: "Preserves the open horse-country sightlines that define greater Aiken." },
    ],
    nearbyAreas: ["Aiken", "Wagener", "Montmorenci", "Windsor", "Beech Island", "Graniteville", "Vaucluse", "Warrenville", "Salley", "Perry", "Edgefield", "North Augusta"],
    localFaqs: [
      ...sharedFaqBase("Aiken", "South Carolina"),
      {
        q: "Do you install on working training operations in Aiken?",
        a: "Yes — working equestrian properties are a core part of our Aiken work. We design perimeters that integrate with paddock fencing, training tracks, gallops, and barn access without interfering with daily horse operations.",
      },
      {
        q: "Will the fence handle feral hog pressure on a rural Aiken County parcel?",
        a: "Our system is engineered for deer and dog containment first, but the tensioned mesh and tight ground clearance we spec in Aiken County hold up far better than typical residential fencing under incidental hog pressure.",
      },
    ],
    lat: 33.5604,
    lng: -81.7196,
  },
];

export const getLocationBySlug = (slug: string) => locations.find((l) => l.slug === slug);
export const locationsByRegion = () => {
  const grouped: Record<Region, LocationData[]> = { Northeast: [], "Mid-Atlantic": [], Southeast: [] };
  for (const loc of locations) grouped[loc.region].push(loc);
  return grouped;
};
